/* eslint-disable react/prop-types */
import React, { Component } from "react";
import { Network, DataSet } from "vis";
import {
  Button, Grid, Segment, Loader,
  Modal, Form, Popup, Icon, Dropdown, Input,
  Image, Header
} from "semantic-ui-react";
import { withRouter } from "react-router-dom";
import Constants from "../utilities/Constants";
import Strings from "../utilities/StringSelector";
import Utils from "../utilities/Utils";
import C2S from "canvas2svg-visjs";

const ipRegex = new RegExp("^[0-2][0-9.]*$");

const UtilsInstance = new Utils();

// Vis.js Network
var nodes = new DataSet();
var edges = new DataSet();
var network;
var networkOptions = {};

// Export Image Canvas options
C2S.prototype.circle = CanvasRenderingContext2D.prototype.circle;
C2S.prototype.square = CanvasRenderingContext2D.prototype.square;
C2S.prototype.triangle = CanvasRenderingContext2D.prototype.triangle;
C2S.prototype.triangleDown = CanvasRenderingContext2D.prototype.triangleDown;
C2S.prototype.star = CanvasRenderingContext2D.prototype.star;
C2S.prototype.diamond = CanvasRenderingContext2D.prototype.diamond;
C2S.prototype.roundRect = CanvasRenderingContext2D.prototype.roundRect;
C2S.prototype.ellipse_vis = CanvasRenderingContext2D.prototype.ellipse_vis;
C2S.prototype.database = CanvasRenderingContext2D.prototype.database;
C2S.prototype.arrowEndpoint = CanvasRenderingContext2D.prototype.arrowEndpoint;
C2S.prototype.circleEndpoint = CanvasRenderingContext2D.prototype.circleEndpoint;
C2S.prototype.dashedLine = CanvasRenderingContext2D.prototype.dashedLine;

var updateDeviceColorsSetInterval = "";
var updateConnectionsSetInterval = "";
var updateClickedDeviceSetInterval = "";

const ADD_DEVICE_MODAL_OK_ID = "add-device-button";
const ADD_DEVICE_MODAL_CLOSE_ID = "close-add-device-modal-button";
const ADD_DEVICE_TAG_ID = "add-device-tag";
const ADD_DEVICE_IP_ID = "add-device-ip-id";

const SWITCH_DEVICE_TYPE = "switch";
const TAP_DEVICE_TYPE = "tap";
const PC_DEVICE_TYPE = "pc";
const SERVER_DEVICE_TYPE = "server";
const ACCESS_POINT_TYPE = "access point";
const HMI_TYPE = "hmi";
const ENDPOINT_TYPE = "endpoint";
const FIREWALL_TYPE = "firewall";

const GRID_SIZE = 140;
const INITIAL_TOPOLOGY_ZOOM_FACTOR = 0.84;
const INITIAL_TOPOLOGY_VERTICAL_OFFSET = 18;

const compareTopologyIps = (firstIp, secondIp) => {
  const firstParts = String(firstIp).split(".").map((part) => Number(part));
  const secondParts = String(secondIp).split(".").map((part) => Number(part));

  for (let index = 0; index < Math.max(firstParts.length, secondParts.length); index++) {
    const difference = (firstParts[index] || 0) - (secondParts[index] || 0);
    if (difference !== 0) return difference;
  }

  return String(firstIp).localeCompare(String(secondIp));
};

const getTopologyPortOrder = (portName) => {
  const match = String(portName || "").match(/\d+/);
  return match ? Number(match[0]) : Number.MAX_SAFE_INTEGER;
};

const compareTopologyNeighbors = (firstNeighbor, secondNeighbor) => {
  const portDifference = getTopologyPortOrder(firstNeighbor.port) - getTopologyPortOrder(secondNeighbor.port);
  if (portDifference !== 0) return portDifference;

  return compareTopologyIps(firstNeighbor.ip, secondNeighbor.ip);
};

const isValidAutomaticPosition = (savedNode) => (
  savedNode
  && savedNode.manualDevice !== true
  && savedNode.graphicItemType !== "region"
  && Number.isFinite(Number(savedNode.x))
  && Number.isFinite(Number(savedNode.y))
  && !!(savedNode.ip || savedNode.id)
);

export const hasSavedAutomaticNodePositions = (savedNodePositions = []) => (
  savedNodePositions.some(isValidAutomaticPosition)
);

/*
 * Produces an initial, repeatable arrangement only for automatic devices. The
 * detected connections are undirected, so a controller (then a switch, then
 * the most connected device) is used as the visual root rather than implying
 * traffic direction. Pure rings keep their circular form; every other
 * component is shown in top-down layers.
 */
export const calculateInitialTopologyLayout = ({
  topologyDevices = [],
  devicesThatHaveAConnection = [],
  liveList = [],
  switchList = []
} = {}) => {
  const connectedDevices = new Set(devicesThatHaveAConnection);
  const devicesByIp = {};

  topologyDevices.forEach((device) => {
    if (!device || !device.ip || device.ip === Strings.ZERO_IP) return;
    if (!connectedDevices.has(device.ip)) return;
    devicesByIp[device.ip] = device;
  });

  const deviceIps = Object.keys(devicesByIp).sort(compareTopologyIps);
  const adjacency = {};
  deviceIps.forEach((ip) => { adjacency[ip] = []; });

  const addNeighbor = (fromIp, toIp, port) => {
    if (!adjacency[fromIp] || !adjacency[toIp]) return;
    if (adjacency[fromIp].some((neighbor) => neighbor.ip === toIp)) return;
    adjacency[fromIp].push({ ip: toIp, port });
  };

  deviceIps.forEach((deviceIp) => {
    const device = devicesByIp[deviceIp];
    (device.connections || []).forEach((connection) => {
      const neighborIp = connection && connection.neighborIP;
      if (!neighborIp || neighborIp === Constants.NONE || !devicesByIp[neighborIp]) return;

      addNeighbor(deviceIp, neighborIp, connection.selfPort);
      addNeighbor(neighborIp, deviceIp, connection.neighborPort);
    });
  });

  Object.keys(adjacency).forEach((ip) => {
    adjacency[ip].sort(compareTopologyNeighbors);
  });

  const liveDevicesByIp = {};
  liveList.forEach((device) => {
    if (device && device.ip) liveDevicesByIp[device.ip] = device;
  });

  const switchesByIp = {};
  switchList.forEach((device) => {
    if (device && device.ip) switchesByIp[device.ip] = device.switch === true;
  });

  const selectComponentRoot = (componentIps) => {
    const sortedIps = [...componentIps].sort((firstIp, secondIp) => {
      const firstIsController = String((liveDevicesByIp[firstIp] || {}).role || "").includes("IO-Controller");
      const secondIsController = String((liveDevicesByIp[secondIp] || {}).role || "").includes("IO-Controller");
      if (firstIsController !== secondIsController) return firstIsController ? -1 : 1;

      const firstIsSwitch = switchesByIp[firstIp] === true;
      const secondIsSwitch = switchesByIp[secondIp] === true;
      if (firstIsSwitch !== secondIsSwitch) return firstIsSwitch ? -1 : 1;

      const degreeDifference = adjacency[secondIp].length - adjacency[firstIp].length;
      if (degreeDifference !== 0) return degreeDifference;

      return compareTopologyIps(firstIp, secondIp);
    });

    return sortedIps[0];
  };

  const getComponents = () => {
    const visited = new Set();
    const components = [];

    deviceIps.forEach((startingIp) => {
      if (visited.has(startingIp)) return;

      const component = [];
      const queue = [startingIp];
      visited.add(startingIp);

      while (queue.length > 0) {
        const currentIp = queue.shift();
        component.push(currentIp);

        adjacency[currentIp].forEach(({ ip: neighborIp }) => {
          if (visited.has(neighborIp)) return;
          visited.add(neighborIp);
          queue.push(neighborIp);
        });
      }

      components.push(component.sort(compareTopologyIps));
    });

    return components;
  };

  const isPureRing = (componentIps) => (
    componentIps.length >= 3
    && componentIps.every((ip) => adjacency[ip].filter((neighbor) => componentIps.includes(neighbor.ip)).length === 2)
  );

  const positions = {};
  let componentOffsetX = 0;
  const componentGap = GRID_SIZE * 4;

  getComponents().forEach((componentIps) => {
    const rootIp = selectComponentRoot(componentIps);
    const controllerIps = componentIps
      .filter((ip) => String((liveDevicesByIp[ip] || {}).role || "").includes("IO-Controller"))
      .sort(compareTopologyIps);

    if (isPureRing(componentIps)) {
      const ringOrder = [];
      let previousIp = null;
      let currentIp = rootIp;

      for (let index = 0; index < componentIps.length; index++) {
        ringOrder.push(currentIp);

        const previousIpForCurrentNode = previousIp;
        const nextIp = adjacency[currentIp]
          .map((neighbor) => neighbor.ip)
          .find((neighborIp) => neighborIp !== previousIpForCurrentNode);

        previousIp = currentIp;
        currentIp = nextIp;
      }

      const rawRadius = (ringOrder.length * GRID_SIZE) / (2 * Math.PI);

      const radius = Math.max(
        GRID_SIZE * 2,
        Math.ceil(rawRadius / GRID_SIZE) * GRID_SIZE
      );
      ringOrder.forEach((ip, index) => {
        const angle = (-Math.PI / 2) + ((2 * Math.PI * index) / ringOrder.length);
        positions[ip] = {
          x: componentOffsetX + (Math.cos(angle) * radius),
          y: radius + (Math.sin(angle) * radius)
        };
      });
      componentOffsetX += (radius * 2) + componentGap;
      return;
    }

    const rootLayer = controllerIps.length > 0 ? controllerIps : [rootIp];
    const visited = new Set(rootLayer);
    const levels = [rootLayer];

    for (let levelIndex = 0; levelIndex < levels.length; levelIndex++) {
      const nextLevel = [];

      levels[levelIndex].forEach((ip) => {
        adjacency[ip].forEach((neighbor) => {
          if (visited.has(neighbor.ip)) return;
          visited.add(neighbor.ip);
          nextLevel.push(neighbor.ip);
        });
      });

      if (nextLevel.length > 0) levels.push(nextLevel);
    }

    const horizontalSpacing = GRID_SIZE * 2;
    const verticalSpacing = GRID_SIZE * 2;
    const widestLevel = Math.max(...levels.map((level) => level.length));

    levels.forEach((level, levelIndex) => {
      level.forEach((ip, index) => {
        positions[ip] = {
          x: componentOffsetX + ((index - ((level.length - 1) / 2)) * horizontalSpacing),
          y: levelIndex * verticalSpacing
        };
      });
    });

    componentOffsetX += Math.max(GRID_SIZE * 4, (widestLevel * horizontalSpacing)) + componentGap;
  });

  const positionedIps = Object.keys(positions);
  if (positionedIps.length === 0) return positions;

  const minX = Math.min(...positionedIps.map((ip) => positions[ip].x));
  const maxX = Math.max(...positionedIps.map((ip) => positions[ip].x));
  const xAdjustment = -((minX + maxX) / 2);

  positionedIps.forEach((ip) => {
    positions[ip].x = Math.round((positions[ip].x + xAdjustment) / GRID_SIZE) * GRID_SIZE;
    positions[ip].y = Math.round(positions[ip].y / GRID_SIZE) * GRID_SIZE;
  });

  return positions;
};

const typeOptions = [
  {
    key: SWITCH_DEVICE_TYPE,
    text: Strings.SWITCH,
    value: SWITCH_DEVICE_TYPE
  },
  {
    key: TAP_DEVICE_TYPE,
    text: Strings.TAP,
    value: TAP_DEVICE_TYPE
  },
  {
    key: PC_DEVICE_TYPE,
    text: Strings.PC,
    value: PC_DEVICE_TYPE
  },
  {
    key: SERVER_DEVICE_TYPE,
    text: Strings.SERVER,
    value: SERVER_DEVICE_TYPE
  },
  {
    key: ACCESS_POINT_TYPE,
    text: Strings.ACCESS_POINT,
    value: ACCESS_POINT_TYPE
  },
  {
    key: HMI_TYPE,
    text: Strings.HMI,
    value: HMI_TYPE
  },
  {
    key: ENDPOINT_TYPE,
    text: Strings.ENDPOINT,
    value: ENDPOINT_TYPE
  },
  {
    key: FIREWALL_TYPE,
    text: Strings.FIREWALL,
    value: FIREWALL_TYPE
  }
];

const locales_pt_br = {
  "pt-br": {
    edit: Strings.LOCALE_EDIT,
    del: Strings.LOCALE_DEL,
    back: Strings.LOCALE_BACK,
    addNode: Strings.LOCALE_ADD_NODE,
    addEdge: Strings.LOCALE_ADD_EDGE,
    editNode: Strings.LOCALE_EDIT_NODE,
    editEdge: Strings.LOCALE_EDIT_EDGE,
    addDescription: Strings.LOCALE_ADD_DESCRIPTION,
    edgeDescription: Strings.LOCALE_EDGE_DESCRIPTION,
    editEdgeDescription: Strings.LOCALE_EDIT_EDGE_DESCRIPTION,
    createEdgeError: Strings.LOCALE_CREATE_EDGE_ERROR,
    deleteClusterError: Strings.LOCALE_DELETE_CLUSTER_ERROR,
    editClusterError: Strings.LOCALE_EDIT_CLUSTER_ERROR
  }
};

let saveTopologyImg = "";
let graphicTopologyInImg = "";
let graphicTopologyOutImg = "";

saveTopologyImg = `${process.env.PUBLIC_URL}/images/save-position.png`;
graphicTopologyInImg = `${process.env.PUBLIC_URL}/images/graphic-topology-in.png`;
graphicTopologyOutImg = `${process.env.PUBLIC_URL}/images/graphic-topology-out.png`;

class GraphicTopology extends Component {
  hasAppliedInitialTopologyZoom = false;
  lockedTopologyCamera = null;
  isUserCameraInteractionActive = false;
  isRestoringTopologyCamera = false;
  cameraChangeAllowedUntil = 0;
  cameraInteractionTimeout = null;
  searchedTopologyNodeId = null;
  searchHighlightStartedAt = 0;
  searchHighlightAnimationFrame = null;
  searchHighlightTimeout = null;
  topologyResizeTimeout = null;

  // Intialize this component state
  constructor(props) {
    super(props);
    this.state = {
      formInput: "",
      topologyDevices: [],
      liveList: [],
      traffic: [],
      savedNodePositions: [],
      devicesThatHaveAConnection: [],
      isLoadingActive: false,
      showModal: false,
      switchList: [],
      isAddDeviceModalOpen: false,
      addDeviceTagInputValue: "",
      addDeviceIpInputValue: "",
      deviceType: "",
      isEditingManualDevice: false,
      editingManualDeviceData: null,
      editingManualDeviceCallback: null,
      isSaveTopologyModalOpen: false,
      topologyRegions: [],
      isAddingTopologyRegion: false,
      topologyRegionStartPoint: null,
      isTopologyRegionModalOpen: false,
      topologyRegionNameInputValue: "",
      isEditingTopologyRegion: false,
      pendingTopologyRegion: null,
      selectedTopologyRegionId: "",
      showDeviceIpOnTopology: true,
      showDeviceTagOnTopology: false,
      showDeviceProfnameOnTopology: false,
      isTopologyDisplayConfigLoading: true,
      isTopologyDisplayConfigSaving: false,
      activeTopologySidebarTab: "position",
      selectedDevice: {
        name: "",
        model: "",
        manufacturer: "",
        ip: "",
        mac: "",
        role: "",
        pingCurrent: "",
        pingMax: "",
        // Possui eventos no expert
        hasTsExpertEvents: "",
      }
    };

    this.topologyRegionInteraction = null;
    this.topologyRegionCreation = null;
    this.topologyRegionPointerDownHandled = false;
    this.isTopologyNetworkDraggingEnabled = true;
  }

  snapToGrid = (value) => Math.round(value / GRID_SIZE) * GRID_SIZE;

  snapAllNodesToGrid = () => {
    if (!network) return;

    network.storePositions();

    const updates = [];
    nodes.forEach((node) => {
      updates.push({
        id: node.id,
        x: this.snapToGrid(node.x),
        y: this.snapToGrid(node.y)
      });
    });

    if (updates.length > 0) {
      nodes.update(updates);
    }
  };


  getSavedTopologyRegions = (savedGraphicItems) => {
    if (!Array.isArray(savedGraphicItems)) return [];

    return savedGraphicItems
      .filter((item) => item && (item.graphicItemType === "region" || item.type === "region"))
      .map((region) => this.normalizeTopologyRegion(region))
      .filter((region) => region !== null);
  };

  normalizeTopologyRegion = (region) => {
    if (!region) return null;

    const x1 = Number(region.x1);
    const y1 = Number(region.y1);
    const x2 = Number(region.x2);
    const y2 = Number(region.y2);

    if ([x1, y1, x2, y2].some((value) => Number.isNaN(value))) {
      return null;
    }

    return {
      graphicItemType: "region",
      id: region.id || ("region-" + Date.now() + "-" + Math.random().toString(36).slice(2, 8)),
      name: region.name || Strings.GRAPHIC_TOPOLOGY_DEFAULT_REGION_NAME,
      x1: Math.min(x1, x2),
      y1: Math.min(y1, y2),
      x2: Math.max(x1, x2),
      y2: Math.max(y1, y2)
    };
  };

  getTopologyRegionOptions = () => {
    return this.state.topologyRegions.map((region) => ({
      key: region.id,
      text: region.name,
      value: region.id
    }));
  };

  onTopologyRegionDropdownChange = (e, { value }) => {
    this.setState({ selectedTopologyRegionId: value });
  };


  getTopologyRegionCanvasPointFromEvent = (event) => {
    if (!network || !network.body || !network.body.container) return null;

    const domPoint = this.getTopologyRegionDomPointFromEvent(event);
    if (!domPoint) return null;

    return network.DOMtoCanvas(domPoint);
  };

  getTopologyRegionDomPointFromEvent = (event) => {
    if (!network || !network.body || !network.body.container) return null;

    const containerRect = network.body.container.getBoundingClientRect();
    return {
      x: event.clientX - containerRect.left,
      y: event.clientY - containerRect.top
    };
  };

  isTopologyEventOnNode = (event) => {
    if (!network || !event) return false;

    const domPoint = this.getTopologyRegionDomPointFromEvent(event);
    if (!domPoint) return false;

    return Boolean(network.getNodeAt(domPoint));
  };

  getTopologyRegionHandleSize = () => {
    if (!network || !network.getScale) return 12;
    return 12 / network.getScale();
  };

  getTopologyRegionHandlePoints = (region) => {
    const centerX = region.x1 + ((region.x2 - region.x1) / 2);
    const centerY = region.y1 + ((region.y2 - region.y1) / 2);

    return [
      { handle: "nw", x: region.x1, y: region.y1 },
      { handle: "n", x: centerX, y: region.y1 },
      { handle: "ne", x: region.x2, y: region.y1 },
      { handle: "e", x: region.x2, y: centerY },
      { handle: "se", x: region.x2, y: region.y2 },
      { handle: "s", x: centerX, y: region.y2 },
      { handle: "sw", x: region.x1, y: region.y2 },
      { handle: "w", x: region.x1, y: centerY }
    ];
  };

  getTopologyRegionHit = (point) => {
    if (!point || !this.state.topologyRegions || this.state.topologyRegions.length === 0) return null;

    const tolerance = this.getTopologyRegionHandleSize();
    const selectedRegion = this.state.topologyRegions.find((region) => region.id === this.state.selectedTopologyRegionId);

    if (selectedRegion) {
      const handleHit = this.getTopologyRegionHandlePoints(selectedRegion).find((handlePoint) => (
        Math.abs(point.x - handlePoint.x) <= tolerance && Math.abs(point.y - handlePoint.y) <= tolerance
      ));

      if (handleHit) {
        return {
          id: selectedRegion.id,
          mode: "resize",
          handle: handleHit.handle
        };
      }
    }

    for (let i = this.state.topologyRegions.length - 1; i >= 0; i--) {
      const region = this.state.topologyRegions[i];
      const isInsideRegion = point.x >= region.x1 && point.x <= region.x2 && point.y >= region.y1 && point.y <= region.y2;

      if (isInsideRegion) {
        return {
          id: region.id,
          mode: "move",
          handle: "move"
        };
      }
    }

    return null;
  };

  getTopologyRegionCursor = (hit) => {
    if (!hit) return "default";

    const cursors = {
      nw: "nwse-resize",
      se: "nwse-resize",
      ne: "nesw-resize",
      sw: "nesw-resize",
      n: "ns-resize",
      s: "ns-resize",
      e: "ew-resize",
      w: "ew-resize",
      move: "move"
    };

    return cursors[hit.handle] || "default";
  };

  setTopologyNetworkDraggingEnabled = (isEnabled) => {
    if (!network || this.isTopologyNetworkDraggingEnabled === isEnabled) return;

    this.isTopologyNetworkDraggingEnabled = isEnabled;
    network.setOptions({
      interaction: {
        hover: true,
        dragView: isEnabled,
        dragNodes: isEnabled
      }
    });
  };

  getResizedTopologyRegion = (region, interaction, point) => {
    const minimumSize = 80;
    const deltaX = point.x - interaction.startPoint.x;
    const deltaY = point.y - interaction.startPoint.y;
    let { x1, y1, x2, y2 } = interaction.originalRegion;

    if (interaction.mode === "move") {
      return {
        ...region,
        x1: x1 + deltaX,
        y1: y1 + deltaY,
        x2: x2 + deltaX,
        y2: y2 + deltaY
      };
    }

    if (interaction.handle.includes("w")) x1 += deltaX;
    if (interaction.handle.includes("e")) x2 += deltaX;
    if (interaction.handle.includes("n")) y1 += deltaY;
    if (interaction.handle.includes("s")) y2 += deltaY;

    if ((x2 - x1) < minimumSize) {
      if (interaction.handle.includes("w")) x1 = x2 - minimumSize;
      else x2 = x1 + minimumSize;
    }

    if ((y2 - y1) < minimumSize) {
      if (interaction.handle.includes("n")) y1 = y2 - minimumSize;
      else y2 = y1 + minimumSize;
    }

    return {
      ...region,
      x1,
      y1,
      x2,
      y2
    };
  };

  handleTopologyRegionMouseDown = (event) => {
    if (!network) return;

    if (event.type === "mousedown" && this.topologyRegionPointerDownHandled) {
      return;
    }

    const canvasPoint = this.getTopologyRegionCanvasPointFromEvent(event);
    if (!canvasPoint) return;

    // Do not start region creation or editing when the user clicks directly on a device.
    // This keeps devices draggable even when they are inside a region.
    if (this.isTopologyEventOnNode(event)) {
      this.setTopologyNetworkDraggingEnabled(true);
      if (network.body && network.body.container) {
        network.body.container.style.cursor = "default";
      }
      return;
    }

    if (this.state.isAddingTopologyRegion) {
      event.preventDefault();
      event.stopPropagation();
      if (event.nativeEvent && event.nativeEvent.stopImmediatePropagation) {
        event.nativeEvent.stopImmediatePropagation();
      }
      else if (event.stopImmediatePropagation) {
        event.stopImmediatePropagation();
      }

      this.topologyRegionCreation = {
        startPoint: canvasPoint
      };

      if (event.type === "pointerdown") this.topologyRegionPointerDownHandled = true;
      network.body.container.style.cursor = "crosshair";
      this.setTopologyNetworkDraggingEnabled(false);
      const initialPendingTopologyRegion = this.normalizeTopologyRegion({
        x1: canvasPoint.x,
        y1: canvasPoint.y,
        x2: canvasPoint.x + 1,
        y2: canvasPoint.y + 1,
        name: ""
      });

      this.setState({
        topologyRegionStartPoint: canvasPoint,
        pendingTopologyRegion: initialPendingTopologyRegion
      }, () => {
        if (network) network.redraw();
      });
      return;
    }

    const hit = this.getTopologyRegionHit(canvasPoint);
    if (!hit) return;

    const originalRegion = this.state.topologyRegions.find((region) => region.id === hit.id);
    if (!originalRegion) return;

    event.preventDefault();
    event.stopPropagation();
    if (event.nativeEvent && event.nativeEvent.stopImmediatePropagation) {
      event.nativeEvent.stopImmediatePropagation();
    }
    else if (event.stopImmediatePropagation) {
      event.stopImmediatePropagation();
    }

    this.topologyRegionInteraction = {
      id: hit.id,
      mode: hit.mode,
      handle: hit.handle,
      startPoint: canvasPoint,
      originalRegion: { ...originalRegion }
    };

    if (event.type === "pointerdown") this.topologyRegionPointerDownHandled = true;
    network.body.container.style.cursor = this.getTopologyRegionCursor(hit);
    this.setTopologyNetworkDraggingEnabled(false);
    this.setState({ selectedTopologyRegionId: hit.id }, () => {
      if (network) network.redraw();
    });
  };

  handleTopologyRegionMouseMove = (event) => {
    if (!network) return;

    const canvasPoint = this.getTopologyRegionCanvasPointFromEvent(event);
    if (!canvasPoint) return;

    if (this.state.isAddingTopologyRegion) {
      network.body.container.style.cursor = "crosshair";
      this.setTopologyNetworkDraggingEnabled(false);

      if (this.topologyRegionCreation) {
        event.preventDefault();
        event.stopPropagation();
        if (event.nativeEvent && event.nativeEvent.stopImmediatePropagation) {
          event.nativeEvent.stopImmediatePropagation();
        }
        else if (event.stopImmediatePropagation) {
          event.stopImmediatePropagation();
        }

        const pendingTopologyRegion = this.normalizeTopologyRegion({
          x1: this.topologyRegionCreation.startPoint.x,
          y1: this.topologyRegionCreation.startPoint.y,
          x2: canvasPoint.x,
          y2: canvasPoint.y,
          name: ""
        });

        this.setState({ pendingTopologyRegion }, () => {
          if (network) network.redraw();
        });
      }

      return;
    }

    if (!this.topologyRegionInteraction) {
      // Regions are drawn behind the devices. When the pointer is over a device,
      // the device must have priority over the region so it can be selected,
      // dragged and moved out of the region normally.
      if (this.isTopologyEventOnNode(event)) {
        network.body.container.style.cursor = "default";
        this.setTopologyNetworkDraggingEnabled(true);
        return;
      }

      const hit = this.getTopologyRegionHit(canvasPoint);
      network.body.container.style.cursor = this.getTopologyRegionCursor(hit);
      this.setTopologyNetworkDraggingEnabled(!hit);
      return;
    }

    event.preventDefault();

    const interaction = this.topologyRegionInteraction;
    this.setState((prevState) => ({
      topologyRegions: prevState.topologyRegions.map((region) => {
        if (region.id !== interaction.id) return region;
        return this.normalizeTopologyRegion(this.getResizedTopologyRegion(region, interaction, canvasPoint));
      })
    }), () => {
      if (network) network.redraw();
    });
  };

  handleTopologyRegionMouseUp = (event) => {
    if (!network) return;

    if (this.topologyRegionCreation) {
      const canvasPoint = this.getTopologyRegionCanvasPointFromEvent(event);
      const creation = this.topologyRegionCreation;
      this.topologyRegionCreation = null;
      this.topologyRegionPointerDownHandled = false;
      network.body.container.style.cursor = "default";
      this.setTopologyNetworkDraggingEnabled(true);

      if (!canvasPoint) {
        this.setState({
          topologyRegionStartPoint: null,
          pendingTopologyRegion: null
        });
        return;
      }

      const pendingTopologyRegion = this.normalizeTopologyRegion({
        x1: creation.startPoint.x,
        y1: creation.startPoint.y,
        x2: canvasPoint.x,
        y2: canvasPoint.y,
        name: ""
      });

      const minimumSize = 40;
      if (!pendingTopologyRegion
        || (pendingTopologyRegion.x2 - pendingTopologyRegion.x1) < minimumSize
        || (pendingTopologyRegion.y2 - pendingTopologyRegion.y1) < minimumSize) {
        this.setState({
          topologyRegionStartPoint: null,
          pendingTopologyRegion: null
        }, () => {
          if (network) network.redraw();
        });
        return;
      }

      this.setState({
        pendingTopologyRegion,
        topologyRegionNameInputValue: "",
        isTopologyRegionModalOpen: true
      }, () => {
        if (network) network.redraw();
      });
      return;
    }

    if (!this.topologyRegionInteraction) return;

    this.topologyRegionInteraction = null;
    this.topologyRegionPointerDownHandled = false;
    network.body.container.style.cursor = "default";
    this.setTopologyNetworkDraggingEnabled(true);
  };

  handleTopologyRegionMouseLeave = () => {
    if (!network || this.topologyRegionInteraction || this.topologyRegionCreation || this.state.isAddingTopologyRegion) return;

    this.topologyRegionPointerDownHandled = false;
    network.body.container.style.cursor = "default";
    this.setTopologyNetworkDraggingEnabled(true);
  };

  onTopologyRegionNameChange = (e, data) => {
    this.setState({ topologyRegionNameInputValue: data.value });
  };

  handleAddTopologyRegionClick = () => {
    if (!network) return;

    clearInterval(updateDeviceColorsSetInterval);
    clearInterval(updateConnectionsSetInterval);
    this.setNetworkOptions(false);

    this.setState({
      isAddingTopologyRegion: true,
      topologyRegionStartPoint: null,
      pendingTopologyRegion: null
    });

    if (network && network.body && network.body.container) {
      network.body.container.style.cursor = "crosshair";
    }

    alert(Strings.GRAPHIC_TOPOLOGY_DRAW_REGION_INSTRUCTION);
  };

  handleCancelTopologyRegionClick = () => {
    this.topologyRegionCreation = null;

    if (network && network.body && network.body.container) {
      this.topologyRegionPointerDownHandled = false;
      network.body.container.style.cursor = "default";
      this.setTopologyNetworkDraggingEnabled(true);
    }

    this.setState({
      isAddingTopologyRegion: false,
      topologyRegionStartPoint: null,
      pendingTopologyRegion: null,
      isTopologyRegionModalOpen: false,
      topologyRegionNameInputValue: "",
      isEditingTopologyRegion: false
    }, () => {
      if (network) network.redraw();
    });
  };

  handleTopologyRegionCanvasClick = (params) => {
    // Region creation is handled by mouse down + drag + mouse up.
    // Returning true here prevents the regular device-selection click from running
    // immediately after finishing a drag-created region.
    if (this.state.isAddingTopologyRegion) {
      return true;
    }

    if (!params || !params.pointer || !params.pointer.canvas) {
      return false;
    }

    if (params.nodes && params.nodes.length > 0) {
      return false;
    }

    const clickedPoint = params.pointer.canvas;
    const clickedRegion = this.getTopologyRegionHit(clickedPoint);

    if (clickedRegion) {
      if (this.state.selectedTopologyRegionId !== clickedRegion.id) {
        this.setState({ selectedTopologyRegionId: clickedRegion.id }, () => {
          if (network) network.redraw();
        });
      }
      return true;
    }

    if (!params.nodes || params.nodes.length === 0) {
      if (this.state.selectedTopologyRegionId) {
        this.setState({ selectedTopologyRegionId: "" }, () => {
          if (network) network.redraw();
        });
      }
    }

    return false;
  };

  handleTopologyRegionModalCancel = () => {
    this.handleCancelTopologyRegionClick();
  };

  handleTopologyRegionModalConfirm = () => {
    const regionName = this.state.topologyRegionNameInputValue.trim();

    if (regionName === "") {
      alert(Strings.GRAPHIC_TOPOLOGY_REGION_NAME_REQUIRED);
      return;
    }

    if (this.state.isEditingTopologyRegion) {
      this.setState((prevState) => ({
        topologyRegions: prevState.topologyRegions.map((region) => {
          if (region.id !== prevState.selectedTopologyRegionId) return region;
          return {
            ...region,
            name: regionName
          };
        }),
        isTopologyRegionModalOpen: false,
        topologyRegionNameInputValue: "",
        isEditingTopologyRegion: false
      }), () => {
        if (network) network.redraw();
      });
      return;
    }

    const newRegion = {
      ...this.state.pendingTopologyRegion,
      id: "region-" + Date.now(),
      name: regionName
    };

    this.setState((prevState) => ({
      topologyRegions: [...prevState.topologyRegions, newRegion],
      selectedTopologyRegionId: newRegion.id,
      isAddingTopologyRegion: false,
      topologyRegionStartPoint: null,
      pendingTopologyRegion: null,
      isTopologyRegionModalOpen: false,
      topologyRegionNameInputValue: "",
      isEditingTopologyRegion: false
    }), () => {
      if (network) network.redraw();
    });
  };

  handleEditTopologyRegionClick = () => {
    const selectedRegion = this.state.topologyRegions.find((region) => region.id === this.state.selectedTopologyRegionId);

    if (!selectedRegion) {
      alert(Strings.GRAPHIC_TOPOLOGY_SELECT_REGION_TO_EDIT);
      return;
    }

    this.setState({
      topologyRegionNameInputValue: selectedRegion.name,
      pendingTopologyRegion: null,
      isAddingTopologyRegion: false,
      isEditingTopologyRegion: true,
      isTopologyRegionModalOpen: true
    });
  };

  handleDeleteTopologyRegionClick = () => {
    if (!this.state.selectedTopologyRegionId) {
      alert(Strings.GRAPHIC_TOPOLOGY_SELECT_REGION_TO_REMOVE);
      return;
    }

    this.setState((prevState) => ({
      topologyRegions: prevState.topologyRegions.filter((region) => region.id !== prevState.selectedTopologyRegionId),
      selectedTopologyRegionId: ""
    }), () => {
      if (network) network.redraw();
    });
  };

  drawTopologyRegions = (ctx) => {
    const topologyRegions = this.state.topologyRegions || [];
    const pendingTopologyRegion = this.state.pendingTopologyRegion;

    if (topologyRegions.length === 0 && !pendingTopologyRegion) return;

    const drawRoundedRect = (context, x, y, width, height, radius) => {
      const safeRadius = Math.min(radius, Math.abs(width) / 2, Math.abs(height) / 2);

      context.beginPath();
      context.moveTo(x + safeRadius, y);
      context.lineTo(x + width - safeRadius, y);
      context.quadraticCurveTo(x + width, y, x + width, y + safeRadius);
      context.lineTo(x + width, y + height - safeRadius);
      context.quadraticCurveTo(x + width, y + height, x + width - safeRadius, y + height);
      context.lineTo(x + safeRadius, y + height);
      context.quadraticCurveTo(x, y + height, x, y + height - safeRadius);
      context.lineTo(x, y + safeRadius);
      context.quadraticCurveTo(x, y, x + safeRadius, y);
      context.closePath();
    };

    const drawRegionLabel = (context, region, isSelected) => {
      const width = region.x2 - region.x1;
      const centerX = region.x1 + (width / 2);
      const labelText = region.name;

      context.font = "bold 27px Roboto, Arial, sans-serif";
      const labelWidth = context.measureText(labelText).width + 36;
      const labelHeight = 34;
      const labelX = centerX - (labelWidth / 2);
      const labelY = region.y1 - 45;

      context.save();
      context.shadowColor = "rgba(15, 23, 42, 0.14)";
      context.shadowBlur = 10;
      context.shadowOffsetY = 4;
      drawRoundedRect(context, labelX, labelY, labelWidth, labelHeight, 17);
      context.fillStyle = isSelected ? "#00b5ad" : "#ffffff";
      context.fill();
      context.shadowColor = "transparent";
      context.lineWidth = 2;
      context.strokeStyle = isSelected ? "#008f89" : "rgba(0, 181, 173, 0.62)";
      context.stroke();
      context.fillStyle = isSelected ? "#ffffff" : "#123536";
      context.textAlign = "center";
      context.textBaseline = "middle";
      context.fillText(labelText, centerX, labelY + (labelHeight / 2) + 1);
      context.restore();
    };

    ctx.save();

    topologyRegions.forEach((region) => {
      const isSelected = region.id === this.state.selectedTopologyRegionId;
      const width = region.x2 - region.x1;
      const height = region.y2 - region.y1;

      ctx.save();
      ctx.shadowColor = isSelected ? "rgba(0, 181, 173, 0.20)" : "rgba(15, 23, 42, 0.08)";
      ctx.shadowBlur = isSelected ? 12 : 7;
      ctx.shadowOffsetY = isSelected ? 4 : 2;
      drawRoundedRect(ctx, region.x1, region.y1, width, height, 18);
      ctx.fillStyle = isSelected ? "rgba(0, 181, 173, 0.075)" : "rgba(0, 181, 173, 0.038)";
      ctx.fill();
      ctx.shadowColor = "transparent";
      ctx.lineWidth = isSelected ? 4 : 3;
      ctx.strokeStyle = isSelected ? "#00b5ad" : "rgba(0, 121, 111, 0.70)";
      ctx.stroke();
      ctx.restore();

      drawRegionLabel(ctx, region, isSelected);

      if (isSelected) {
        const handleSize = this.getTopologyRegionHandleSize();
        ctx.fillStyle = "#ffffff";
        ctx.strokeStyle = "#00b5ad";
        ctx.lineWidth = 2;

        this.getTopologyRegionHandlePoints(region).forEach((handlePoint) => {
          ctx.save();
          ctx.shadowColor = "rgba(0, 181, 173, 0.24)";
          ctx.shadowBlur = 6;
          ctx.shadowOffsetY = 2;
          drawRoundedRect(
            ctx,
            handlePoint.x - (handleSize / 2),
            handlePoint.y - (handleSize / 2),
            handleSize,
            handleSize,
            3
          );
          ctx.fill();
          ctx.shadowColor = "transparent";
          ctx.stroke();
          ctx.restore();
        });
      }
    });

    if (pendingTopologyRegion && this.state.isAddingTopologyRegion) {
      const width = pendingTopologyRegion.x2 - pendingTopologyRegion.x1;
      const height = pendingTopologyRegion.y2 - pendingTopologyRegion.y1;

      ctx.save();
      drawRoundedRect(ctx, pendingTopologyRegion.x1, pendingTopologyRegion.y1, width, height, 16);
      ctx.fillStyle = "rgba(0, 181, 173, 0.105)";
      ctx.fill();
      ctx.lineWidth = 4;
      ctx.strokeStyle = "#00b5ad";
      if (ctx.setLineDash) ctx.setLineDash([14, 10]);
      ctx.stroke();
      if (ctx.setLineDash) ctx.setLineDash([]);
      ctx.restore();

      ctx.font = "bold 24px Roboto, Arial, sans-serif";
      ctx.fillStyle = "#00756f";
      ctx.textAlign = "center";
      ctx.textBaseline = "bottom";
      ctx.fillText(Strings.GRAPHIC_TOPOLOGY_NEW_REGION, pendingTopologyRegion.x1 + (width / 2), pendingTopologyRegion.y1 - 12);

      ctx.font = "18px Roboto, Arial, sans-serif";
      ctx.fillStyle = "#006d68";
      ctx.textBaseline = "top";
      ctx.fillText(`${Math.round(width)} x ${Math.round(height)}`, pendingTopologyRegion.x1 + (width / 2), pendingTopologyRegion.y1 + 12);
    }

    ctx.restore();
  };

  /*
    Saves the form input to the component state everytime the user
    types something in the form
  */
  handleChange = (e, { name, value }) => this.setState({ [name]: value });

  stopSearchNodeHighlight = () => {
    if (this.searchHighlightAnimationFrame !== null) {
      cancelAnimationFrame(this.searchHighlightAnimationFrame);
      this.searchHighlightAnimationFrame = null;
    }

    clearTimeout(this.searchHighlightTimeout);
    this.searchHighlightTimeout = null;
    this.searchedTopologyNodeId = null;

    if (network) network.redraw();
  };

  startSearchNodeHighlight = (nodeId) => {
    this.stopSearchNodeHighlight();
    this.searchedTopologyNodeId = nodeId;
    this.searchHighlightStartedAt = performance.now();

    const redrawHighlight = () => {
      if (!network || this.searchedTopologyNodeId === null) return;
      network.redraw();
      this.searchHighlightAnimationFrame = requestAnimationFrame(redrawHighlight);
    };

    this.searchHighlightAnimationFrame = requestAnimationFrame(redrawHighlight);
    this.searchHighlightTimeout = setTimeout(this.stopSearchNodeHighlight, 4200);
  };

  drawSearchNodeHighlight = (ctx) => {
    if (!network || this.searchedTopologyNodeId === null) return;

    let box;
    try {
      box = network.getBoundingBox(this.searchedTopologyNodeId);
    }
    catch (error) {
      return;
    }

    if (!box) return;

    const elapsed = performance.now() - this.searchHighlightStartedAt;
    const pulse = (Math.sin(elapsed / 170) + 1) / 2;
    const padding = 12 + (pulse * 5);
    const left = box.left - padding;
    const top = box.top - padding;
    const width = (box.right - box.left) + (padding * 2);
    const height = (box.bottom - box.top) + (padding * 2);
    const radius = 12;

    ctx.save();
    ctx.beginPath();
    ctx.moveTo(left + radius, top);
    ctx.lineTo(left + width - radius, top);
    ctx.quadraticCurveTo(left + width, top, left + width, top + radius);
    ctx.lineTo(left + width, top + height - radius);
    ctx.quadraticCurveTo(left + width, top + height, left + width - radius, top + height);
    ctx.lineTo(left + radius, top + height);
    ctx.quadraticCurveTo(left, top + height, left, top + height - radius);
    ctx.lineTo(left, top + radius);
    ctx.quadraticCurveTo(left, top, left + radius, top);
    ctx.closePath();
    ctx.fillStyle = `rgba(0, 181, 173, ${0.08 + (pulse * 0.08)})`;
    ctx.fill();
    ctx.lineWidth = 4;
    ctx.strokeStyle = `rgba(0, 181, 173, ${0.72 + (pulse * 0.28)})`;
    ctx.shadowColor = "rgba(0, 181, 173, 0.75)";
    ctx.shadowBlur = 12 + (pulse * 8);
    ctx.stroke();
    ctx.restore();
  };

  handleSubmit = () => {
    const searchText = String(this.state.formInput || "").trim();

    if (searchText === "") {
      alert(Strings.GRAPHIC_TOPOLOGY_SEARCH_EMPTY);
      return;
    }

    const normalizedSearchText = searchText.toLowerCase();
    const topologyNodes = nodes ? nodes.get() : [];
    const getSearchableValues = (node) => [
      node.id,
      node.ip,
      node.tag,
      node.profname
    ]
      .filter(value => value !== null && value !== undefined && value !== "")
      .map(value => String(value).trim().toLowerCase());

    const foundNode = topologyNodes.find(node => (
      getSearchableValues(node).some(value => value === normalizedSearchText)
    )) || topologyNodes.find(node => (
      getSearchableValues(node).some(value => value.includes(normalizedSearchText))
    ));

    if (!foundNode) {
      alert(Strings.GRAPHIC_TOPOLOGY_SEARCH_NOT_FOUND_1
        + searchText
        + Strings.GRAPHIC_TOPOLOGY_SEARCH_NOT_FOUND_2);
      return;
    }

    this.allowTopologyCameraChange(900);
    network.focus(foundNode.id);
    network.selectNodes([foundNode.id]);
    this.startSearchNodeHighlight(foundNode.id);
    network.fit({
      nodes: [foundNode.id],
      animation: true
    });

    if (foundNode.manualDevice !== true) {
      const selectedDevice = this.state.liveList.find(device => device.ip === foundNode.ip);
      if (selectedDevice !== undefined) {
        this.onDeviceClick(selectedDevice);
      }
    }
  };

  showLoading = () => this.setState({ isLoadingActive: true });
  hideLoading = () => {
    if (this.state.isLoadingActive) {
      this.setState({ isLoadingActive: false });
    }
  };

  retrieveData = async () => {
    this.showLoading();

    let promissesArray = [];

    promissesArray.push(
      fetch(Constants.REQUEST_IP_ADDRESS + "topology", { mode: "cors" })
        .then(res => res.json())
        .then(data => data)
        .catch(() => {
          return [];
        })
    );

    promissesArray.push(
      fetch(Constants.REQUEST_IP_ADDRESS, { mode: "cors" })
        .then(res => res.json())
        .then(data => data)
        .catch(() => {
          return [];
        })
    );

    promissesArray.push(
      fetch(Constants.REQUEST_IP_ADDRESS + "traffic", { mode: "cors" })
        .then(res => res.json())
        .then(data => data.devices)
        .catch(() => {
          return [];
        })
    );

    promissesArray.push(
      fetch(Constants.REQUEST_IP_ADDRESS + "graphicTopology", { mode: "cors" })
        .then(res => res.json())
        .then(data => data)
        .catch(() => {
          return [];
        })
    );

    promissesArray.push(
      fetch(Constants.REQUEST_IP_ADDRESS + "graphicTopologyDevices", { mode: "cors" })
        .then(res => res.json())
        .then(data => data)
        .catch(() => {
          return [];
        })
    );

    promissesArray.push(
      fetch(Constants.REQUEST_IP_ADDRESS + "checkDeviceType", { mode: "cors" })
        .then(res => res.json())
        .then(data => data)
        .catch(() => {
          const fakeSwitchData = this.buildFakeCheckSwitchData();
          return fakeSwitchData;
        })
    );


    let resolvedPromisses = await Promise.all(promissesArray);

    this.setState({
      topologyDevices: resolvedPromisses[0],
      liveList: resolvedPromisses[1],
      traffic: resolvedPromisses[2],
      savedNodePositions: resolvedPromisses[3],
      topologyRegions: this.getSavedTopologyRegions(resolvedPromisses[3]),
      devicesThatHaveAConnection: resolvedPromisses[4],
      switchList: resolvedPromisses[5],
    }, () => {
      if (this.doesNetworkContainDuplicateIP()) {
        alert(Strings.DUPLICATE_IPS);

        this.hideLoading();
      }
      else {
        // Finally, with all this info we can create the network
        this.createNetwork();
      }
    });
  };

  getPingText = (ping) => {
    if (ping === undefined || ping === null || ping === "" || ping === "-") {
      return "-";
    }
    else {
      const lang = localStorage.getItem(Constants.LOCAL_STORAGE_KEY);
      const pingText = (lang !== Constants.EN_LANG_KEY) ? UtilsInstance.replacePointWithComma(ping.toString()) : ping.toString();
      return pingText.concat(" ms");
    }
  };

  getDeviceSelectedStyle = (selectedDeviceMac) => {
    if (selectedDeviceMac !== "") {
      return { display: "block" };
    }
    else {
      return { display: "none" };
    }
  };

  getNoDeviceSelectedStyle = (selectedDeviceMac) => {
    if (selectedDeviceMac !== "") {
      return { display: "none" };
    }
    else {
      return { display: "block" };
    }
  };

  getHasTsExpertEventsText = (hasTsExpertEvents) => {
    if (hasTsExpertEvents === "-") {
      return "-";
    }
    else if (hasTsExpertEvents) {
      return Strings.YES;
    }
    else {
      return Strings.NO;
    }
  };

  // We had a problem where the /checkSwitch route was not working, and that prevented
  // the graphic topology from loading. To be able to load the topology even when
  // this happens we use this function to build the switch list and we assume
  // that every device will have the image of an io-device
  buildFakeCheckSwitchData = () => {
    let switchList = [];

    for (let i = 0; i < this.state.devicesThatHaveAConnection.length; i++) {
      switchList.push({
        "drive": false,
        "ip": this.state.devicesThatHaveAConnection[i],
        "switch": false,
        "wireless": false
      });
    }

    return switchList;
  };

  setNodeImage = (deviceIP) => {
    for (let i = 0; i < this.state.liveList.length; i++) {
      if (deviceIP === this.state.liveList[i].ip) {
        return this.getDeviceImagePath(
          deviceIP,
          this.state.liveList[i].status,
          this.state.liveList[i].role,
          this.state.liveList[i].isIgnoringMonitoring);
      }
    }
    return (Constants.WEB_DEFAULT_IMG_PATH);
  };

  // If the network contains a duplicated IP return true, otherwise return false
  doesNetworkContainDuplicateIP = () => {
    for (let i = 0; i < this.state.liveList.length; i++) {
      if (this.state.liveList[i].status === Constants.DUPLICATE_IP) {
        return true;
      }
    }
    return false;
  };

  getSwitchWirelessDriveData = (ip) => {
    const deviceType = this.state.switchList.find(
      item => item && item.ip === ip
    );

    return {
      switch: Boolean(deviceType && deviceType.switch),
      drive: Boolean(deviceType && deviceType.drive),
      wireless: Boolean(deviceType && deviceType.wireless)
    };
  };

  // Returns the image path corresponding to the device current role and status
  getDeviceImagePath = (ip, status, role, isIgnoringMonitoring) => {
    if (isIgnoringMonitoring) {
      if (role === Constants.ROLE_IO_CONTROLLER) {
        return Constants.IO_CONTROLLER_BLACKLIST_IMG_PATH;
      }
      else if (role === Constants.ROLE_IO_DEVICE) {
        let data = this.getSwitchWirelessDriveData(ip);

        if (data.switch) {
          return Constants.SWITCH_BLACKLIST_IMG_PATH;
        }
        else if (data.drive) {
          return Constants.DRIVE_BLACKLIST_IMG_PATH;
        }
        else if (data.wireless) {
          return Constants.WIRELESS_BLACKLIST_IMG_PATH;
        }
        else {
          return Constants.IO_DEVICE_BLACKLIST_IMG_PATH;
        }
      }
      else if (role === Constants.ROLE_IO_SUPERVISOR) {
        return Constants.IO_SUPERVISOR_BLACKLIST_IMG_PATH;
      }
      else if (role === Constants.ROLE_HMI) {
        return Constants.HMI_BLACKLIST_IMG_PATH;
      }
      else {
        return Constants.IO_DEVICE_BLACKLIST_IMG_PATH;
      }
    }
    else {
      if (status === Constants.OK) {
        if (role === null) {
          return Constants.IO_DEVICE_OK_IMG_PATH;
        }
        else if (role === Constants.ROLE_IO_CONTROLLER) {
          return Constants.IO_CONTROLLER_OK_IMG_PATH;
        }
        else if (role === Constants.ROLE_IO_DEVICE) {
          let data = this.getSwitchWirelessDriveData(ip);

          if (data.switch) {
            return Constants.SWITCH_OK_IMG_PATH;
          }
          else if (data.drive) {
            return Constants.DRIVE_OK_IMG_PATH;
          }
          else if (data.wireless) {
            return Constants.WIRELESS_OK_IMG_PATH;
          }
          else {
            return Constants.IO_DEVICE_OK_IMG_PATH;
          }
        }
        else if (role === Constants.ROLE_IO_SUPERVISOR) {
          return Constants.IO_SUPERVISOR_OK_IMG_PATH;
        }
        else if (role === Constants.ROLE_HMI) {
          return Constants.HMI_OK_IMG_PATH;
        }
        else {
          return Constants.IO_DEVICE_OK_IMG_PATH;
        }
      }
      else if (status === Constants.DIAGNOSTIC) {
        if (role === null) {
          return Constants.IO_DEVICE_DIAGNOSTIC_IMG_PATH;
        }
        else if (role === Constants.ROLE_IO_CONTROLLER) {
          return Constants.IO_CONTROLLER_DIAGNOSTIC_IMG_PATH;
        }
        else if (role === Constants.ROLE_IO_DEVICE) {
          let data = this.getSwitchWirelessDriveData(ip);

          if (data.switch) {
            return Constants.SWITCH_DIAGNOSTIC_IMG_PATH;
          }
          else if (data.drive) {
            return Constants.DRIVE_DIAGNOSTIC_IMG_PATH;
          }
          else if (data.wireless) {
            return Constants.WIRELESS_DIAGNOSTIC_IMG_PATH;
          }
          else {
            return Constants.IO_DEVICE_DIAGNOSTIC_IMG_PATH;
          }
        }
        else if (role === Constants.ROLE_IO_SUPERVISOR) {
          return Constants.IO_SUPERVISOR_DIAGNOSTIC_IMG_PATH;
        }
        else if (role === Constants.ROLE_HMI) {
          return Constants.HMI_DIAGNOSTIC_IMG_PATH;
        }
        else {
          return Constants.IO_DEVICE_DIAGNOSTIC_IMG_PATH;
        }
      }
      else if (status === Constants.FAULT) {
        if (role === null) {
          return Constants.IO_DEVICE_FAULT_IMG_PATH;
        }
        else if (role === Constants.ROLE_IO_CONTROLLER) {
          return Constants.IO_CONTROLLER_FAULT_IMG_PATH;
        }
        else if (role === Constants.ROLE_IO_DEVICE) {
          let data = this.getSwitchWirelessDriveData(ip);

          if (data.switch) {
            return Constants.SWITCH_FAULT_IMG_PATH;
          }
          else if (data.drive) {
            return Constants.DRIVE_FAULT_IMG_PATH;
          }
          else if (data.wireless) {
            return Constants.WIRELESS_FAULT_IMG_PATH;
          }
          else {
            return Constants.IO_DEVICE_FAULT_IMG_PATH;
          }
        }
        else if (role === Constants.ROLE_IO_SUPERVISOR) {
          return Constants.IO_SUPERVISOR_FAULT_IMG_PATH;
        }
        else if (role === Constants.ROLE_HMI) {
          return Constants.HMI_FAULT_IMG_PATH;
        }
        else {
          return Constants.IO_DEVICE_FAULT_IMG_PATH;
        }
      }
      else if (status === Constants.CONFIG_ERROR) {
        if (role === null) {
          return Constants.IO_DEVICE_CONFIG_ERROR_IMG_PATH;
        }
        else if (role === Constants.ROLE_IO_CONTROLLER) {
          return Constants.IO_CONTROLLER_CONFIG_ERROR_IMG_PATH;
        }
        else if (role === Constants.ROLE_IO_DEVICE) {
          let data = this.getSwitchWirelessDriveData(ip);

          if (data.switch) {
            return Constants.SWITCH_CONFIG_ERROR_IMG_PATH;
          }
          else if (data.drive) {
            return Constants.DRIVE_CONFIG_ERROR_IMG_PATH;
          }
          else if (data.wireless) {
            return Constants.WIRELESS_CONFIG_ERROR_IMG_PATH;
          }
          else {
            return Constants.IO_DEVICE_CONFIG_ERROR_IMG_PATH;
          }
        }
        else if (role === Constants.ROLE_IO_SUPERVISOR) {
          return Constants.IO_SUPERVISOR_CONFIG_ERROR_IMG_PATH;
        }
        else if (role === Constants.ROLE_HMI) {
          return Constants.HMI_CONFIG_ERROR_IMG_PATH;
        }
        else {
          return Constants.IO_DEVICE_CONFIG_ERROR_IMG_PATH;
        }
      }
      else if (status === Constants.MISSING) {
        if (role === null) {
          return Constants.IO_DEVICE_MISSING_IMG_PATH;
        }
        else if (role === Constants.ROLE_IO_CONTROLLER) {
          return Constants.IO_CONTROLLER_MISSING_IMG_PATH;
        }
        else if (role === Constants.ROLE_IO_DEVICE) {
          let data = this.getSwitchWirelessDriveData(ip);

          if (data.switch) {
            return Constants.SWITCH_MISSING_IMG_PATH;
          }
          else if (data.drive) {
            return Constants.DRIVE_MISSING_IMG_PATH;
          }
          else if (data.wireless) {
            return Constants.WIRELESS_MISSING_IMG_PATH;
          }
          else {
            return Constants.IO_DEVICE_MISSING_IMG_PATH;
          }
        }
        else if (role === Constants.ROLE_IO_SUPERVISOR) {
          return Constants.IO_SUPERVISOR_MISSING_IMG_PATH;
        }
        else if (role === Constants.ROLE_HMI) {
          return Constants.HMI_MISSING_IMG_PATH;
        }
        else {
          return Constants.IO_DEVICE_MISSING_IMG_PATH;
        }
      }
      else if (status === Constants.DIFF_SUBNET) {
        if (role === null) {
          return Constants.IO_DEVICE_DIFF_SUBNET_PATH;
        }
        else if (role === Constants.ROLE_IO_CONTROLLER) {
          return Constants.IO_CONTROLLER_DIFF_SUBNET_IMG_PATH;
        }
        else if (role === Constants.ROLE_IO_DEVICE) {
          let data = this.getSwitchWirelessDriveData(ip);

          if (data.switch) {
            return Constants.SWITCH_DIFF_SUBNET_IMG_PATH;
          }
          else if (data.drive) {
            return Constants.DRIVE_DIFF_SUBNET_IMG_PATH;
          }
          else if (data.wireless) {
            return Constants.WIRELESS_DIFF_SUBNET_IMG_PATH;
          }
          else {
            return Constants.IO_DEVICE_DIFF_SUBNET_PATH;
          }
        }
        else if (role === Constants.ROLE_IO_SUPERVISOR) {
          return Constants.IO_SUPERVISOR_DIFF_SUBNET_IMG_PATH;
        }
        else if (role === Constants.ROLE_HMI) {
          return Constants.HMI_DIFF_SUBNET_IMG_PATH;
        }
        else {
          return Constants.IO_DEVICE_DIFF_SUBNET_PATH;
        }
      }
      else {
        return Constants.WEB_DEFAULT_IMG_PATH;
      }
    }
  };

  getDeviceXYInPositionArray = (i) => {
    if (this.state.savedNodePositions.length > 0) {
      for (let j = 0; j < this.state.savedNodePositions.length; j++) {
        if (this.state.topologyDevices[i].ip === this.state.savedNodePositions[j].ip) {
          return j;
        }
      }
      return -1;
    }
    else {
      return -1;
    }
  };

  getDeviceXY = (j) => {
    let xyPosition = [];

    xyPosition.push(this.state.savedNodePositions[j].x);
    xyPosition.push(this.state.savedNodePositions[j].y);

    return xyPosition;
  };

  hasSavedAutomaticNodePositions = () => hasSavedAutomaticNodePositions(this.state.savedNodePositions);

  getInitialTopologyLayout = () => calculateInitialTopologyLayout({
    topologyDevices: this.state.topologyDevices,
    devicesThatHaveAConnection: this.state.devicesThatHaveAConnection,
    liveList: this.state.liveList,
    switchList: this.state.switchList
  });

  getDeviceLiveListDataByIp = (deviceIP) => {
    return this.state.liveList.find((device) => device.ip === deviceIP) || {};
  };

  getDeviceTagText = (deviceIP, fallbackDevice = {}) => {
    const liveListDevice = this.getDeviceLiveListDataByIp(deviceIP);
    const tag = liveListDevice.tag || fallbackDevice.tag || "";

    if (tag === null || tag === undefined || tag === "" || tag === "-") {
      return "";
    }

    return String(tag);
  };

  getDeviceProfnameText = (device = {}) => {
    const profname = device.profname
      || device.profName
      || device.profiname
      || device.name
      || "";

    if (profname === null || profname === undefined || profname === "" || profname === "-" || profname === Constants.NONE) {
      return "";
    }

    return String(profname);
  };

  getAutomaticDeviceLabel = (device = {}) => {
    const deviceIP = device.ip || "";
    const labelLines = [];

    if (this.state.showDeviceIpOnTopology && deviceIP !== "") {
      labelLines.push(deviceIP);
    }

    if (this.state.showDeviceTagOnTopology) {
      const tag = this.getDeviceTagText(deviceIP, device);
      if (tag !== "") {
        labelLines.push(tag);
      }
    }

    if (this.state.showDeviceProfnameOnTopology) {
      const profname = this.getDeviceProfnameText(device);
      if (profname !== "") {
        labelLines.push(profname);
      }
    }

    return labelLines.join("\n");
  };

  updateAutomaticDeviceLabels = () => {
    if (!nodes) return;

    const updates = [];
    nodes.forEach((node) => {
      if (node.manualDevice === true) return;

      const deviceIP = node.ip || node.id;
      const topologyDevice = this.state.topologyDevices.find((device) => device.ip === deviceIP) || node;
      updates.push({
        id: node.id,
        label: this.getAutomaticDeviceLabel(topologyDevice),
        tag: this.getDeviceTagText(deviceIP, topologyDevice),
        profname: this.getDeviceProfnameText(topologyDevice)
      });
    });

    if (updates.length > 0) {
      nodes.update(updates);
    }

    if (network) network.redraw();
  };

  retrieveGraphicTopologyDisplayConfig = async () => {
    try {
      const response = await fetch(
        Constants.REQUEST_IP_ADDRESS + "graphicTopologyDisplayConfig",
        { mode: "cors" }
      );

      if (!response.ok) {
        throw new Error("HTTP " + response.status);
      }

      const config = await response.json();
      const showIp = typeof config.showIp === "boolean" ? config.showIp : true;
      const showTag = typeof config.showTag === "boolean" ? config.showTag : false;
      const showProfiname = typeof config.showProfiname === "boolean"
        ? config.showProfiname
        : false;

      this.setState({
        showDeviceIpOnTopology: showIp,
        showDeviceTagOnTopology: showTag,
        showDeviceProfnameOnTopology: showProfiname,
        isTopologyDisplayConfigLoading: false
      }, this.updateAutomaticDeviceLabels);
    }
    catch (error) {
      this.setState({
        showDeviceIpOnTopology: true,
        showDeviceTagOnTopology: false,
        showDeviceProfnameOnTopology: false,
        isTopologyDisplayConfigLoading: false
      }, this.updateAutomaticDeviceLabels);
    }
  };

  saveGraphicTopologyDisplayConfig = async () => {
    if (this.state.isTopologyDisplayConfigSaving) return;

    this.setState({ isTopologyDisplayConfigSaving: true });

    try {
      const response = await fetch(
        Constants.REQUEST_IP_ADDRESS + "graphicTopologyDisplayConfig_POST",
        {
          method: "POST",
          mode: "cors",
          headers: {
            "Content-Type": "application/json"
          },
          body: JSON.stringify({
            showIp: this.state.showDeviceIpOnTopology,
            showTag: this.state.showDeviceTagOnTopology,
            showProfiname: this.state.showDeviceProfnameOnTopology
          })
        }
      );

      if (!response.ok) {
        throw new Error("HTTP " + response.status);
      }

      alert(Strings.GRAPHIC_TOPOLOGY_DISPLAY_CONFIG_SAVED);
    }
    catch (error) {
      alert(Strings.GRAPHIC_TOPOLOGY_DISPLAY_CONFIG_SAVE_ERROR);
    }
    finally {
      this.setState({ isTopologyDisplayConfigSaving: false });
    }
  };

  handleShowDeviceIpOnTopologyChange = (e, data) => {
    this.setState({ showDeviceIpOnTopology: data.checked }, this.updateAutomaticDeviceLabels);
  };

  handleShowDeviceTagOnTopologyChange = (e, data) => {
    this.setState({ showDeviceTagOnTopology: data.checked }, this.updateAutomaticDeviceLabels);
  };

  handleShowDeviceProfnameOnTopologyChange = (e, data) => {
    this.setState({ showDeviceProfnameOnTopology: data.checked }, this.updateAutomaticDeviceLabels);
  };

  createNodeTitle = (device) => {
    let name = "";
    let model = "";
    let nodeTitle = "";

    if (device.name !== null || device.name !== undefined || device.name !== "" || device.name !== "-" || device.name !== Constants.NONE) {
      name = device.name;
    }

    if (device.model !== null) {
      model = device.model;
    }

    nodeTitle = "<p style='text-align: justify;'> " + Strings.NAME_DP + name
      + "<br />" + Strings.MODEL_DP + model + "</p >";

    return nodeTitle;
  };

  onDeviceClick = async (device) => {
    this.setState({ activeTopologySidebarTab: "device" });

    let deviceInfo = await fetch(Constants.REQUEST_IP_ADDRESS + "deviceInfo/" + device.mac, { mode: "cors" })
      .then(UtilsInstance.checkStatus)
      .then(res => res.json())
      .then(data => data)
      .catch((error) => {
        console.log(error);
        return {
          name: "",
          model: "",
          manufacturer: "",
          ip: "",
          mac: "",
          role: "",
          pingCurrent: "",
          pingMax: "",
          // Possui eventos no expert
          hasTsExpertEvents: "",
        };
      });

    clearInterval(updateClickedDeviceSetInterval);

    this.setState({ selectedDevice: deviceInfo }, () => {
      updateClickedDeviceSetInterval = setInterval(this.updateSelectedDevice, Constants.SELECTED_DEVICE_REFRESH_TIME);
    });
  };

  createNodes = () => {
    nodes = new DataSet();
    const hasSavedPositions = this.hasSavedAutomaticNodePositions();
    const initialTopologyLayout = hasSavedPositions ? {} : this.getInitialTopologyLayout();

    for (let i = 0; i < this.state.topologyDevices.length; i++) {
      // If this device has connections we will try to create its node
      if (this.state.devicesThatHaveAConnection.indexOf(this.state.topologyDevices[i].ip) !== -1) {
        // If this device X and Y position in the screen has been saved we use it
        let deviceXYPositionInArray = this.getDeviceXYInPositionArray(i);
        if (deviceXYPositionInArray !== -1) {
          let xyPosition = this.getDeviceXY(deviceXYPositionInArray);
          let imagePath = this.setNodeImage(this.state.topologyDevices[i].ip);
          // Only add the device if it has an IP address that is different from 0.0.0.0
          if (this.state.topologyDevices[i].ip !== Strings.ZERO_IP) {
            nodes.add({
              id: this.state.topologyDevices[i].ip,
              label: this.getAutomaticDeviceLabel(this.state.topologyDevices[i]),
              ip: this.state.topologyDevices[i].ip,
              tag: this.getDeviceTagText(this.state.topologyDevices[i].ip, this.state.topologyDevices[i]),
              profname: this.getDeviceProfnameText(this.state.topologyDevices[i]),
              manualDevice: false,
              shape: "image",
              image: imagePath,
              x: this.snapToGrid(xyPosition[0]),
              y: this.snapToGrid(xyPosition[1]),
              shapeProperties: {
                useImageSize: false
              },
              size: 34,
              font: {
                face: "Roboto",
                size: 16,
                color: "#263238",
                strokeWidth: 4,
                strokeColor: "#ffffff"
              },
              title: this.createNodeTitle(this.state.topologyDevices[i])
            });
          }
        }
        // Otherwise, let the visualization library choose the X and Y position by itself
        else {
          let imagePath = this.setNodeImage(this.state.topologyDevices[i].ip);
          const automaticPosition = initialTopologyLayout[this.state.topologyDevices[i].ip];
          // Only add the device if it has an IP address that is different from 0.0.0.0
          if (this.state.topologyDevices[i].ip !== Strings.ZERO_IP) {
            nodes.add({
              id: this.state.topologyDevices[i].ip,
              label: this.getAutomaticDeviceLabel(this.state.topologyDevices[i]),
              ip: this.state.topologyDevices[i].ip,
              tag: this.getDeviceTagText(this.state.topologyDevices[i].ip, this.state.topologyDevices[i]),
              profname: this.getDeviceProfnameText(this.state.topologyDevices[i]),
              manualDevice: false,
              shape: "image",
              image: imagePath,
              x: automaticPosition ? automaticPosition.x : undefined,
              y: automaticPosition ? automaticPosition.y : undefined,
              shapeProperties: {
                useImageSize: false
              },
              size: 34,
              font: {
                face: "Roboto",
                size: 16,
                color: "#263238",
                strokeWidth: 4,
                strokeColor: "#ffffff"
              },
              title: this.createNodeTitle(this.state.topologyDevices[i])
            });
          }
        }
      }
    }

    // Restore devices added manually through the vis.js manipulation menu.
    // They are not present in topologyDevices, so they must be recreated from
    // the saved graphic topology positions.
    this.state.savedNodePositions.forEach((savedNode) => {
      if (savedNode.manualDevice !== true) return;

      const savedNodeId = savedNode.id || savedNode.ip;
      if (!savedNodeId || nodes.get(savedNodeId) !== null) return;

      nodes.add({
        id: savedNodeId,
        label: savedNode.label || savedNode.tag || savedNodeId,
        shape: savedNode.shape || "image",
        image: savedNode.image || this.getDeviceImagePathBasedOnDeviceType(savedNode.deviceType),
        x: this.snapToGrid(savedNode.x),
        y: this.snapToGrid(savedNode.y),
        deviceType: savedNode.deviceType || "",
        manualDevice: true,
        tag: savedNode.tag || "",
        manualIP: savedNode.manualIP || "",
        ip: savedNode.ip || savedNodeId,
        shapeProperties: {
          useImageSize: false
        },
        size: 34,
        font: {
          face: "Roboto",
          size: 16,
          color: "#263238",
          strokeWidth: 4,
          strokeColor: "#ffffff"
        },
        title: savedNode.label || savedNode.tag || savedNodeId
      });
    });
  };

  // Returns only the numeric part of the port name for edge labels, without leading zeros.
  // Examples: "Port 001" -> "1", "port-002" -> "2", "P003" -> "3".
  getPortNumberLabel = (portName) => {
    if (portName === undefined || portName === null || portName === "") {
      return "-";
    }

    const match = String(portName).match(/\d+/);
    if (!match) {
      return String(portName);
    }

    const parsedPort = parseInt(match[0], 10);
    return Number.isNaN(parsedPort) ? match[0] : String(parsedPort);
  };

  // Returns the same key for A -> B and B -> A. Used to know how many physical
  // links exist between the same two devices. If there is more than one unique
  // link between the same pair of IPs, the topology draws them as a redundant ring.
  getConnectionPairKey = (ipA, ipB) => [ipA, ipB].sort().join("<->");

  // Returns the same key for the same physical cable even if the connection is
  // reported from the other device. This avoids drawing the same physical link twice
  // when the backend reports both directions.
  getPhysicalConnectionKey = (ipA, portA, ipB, portB) => {
    const endpointA = `${ipA}/${portA}`;
    const endpointB = `${ipB}/${portB}`;
    return [endpointA, endpointB].sort().join("<->");
  };

  buildConnectionPairCount = () => {
    const pairCount = {};
    const physicalConnections = new Set();

    for (let i = 0; i < this.state.topologyDevices.length; i++) {
      const device = this.state.topologyDevices[i];

      for (let j = 0; j < device.connections.length; j++) {
        const connection = device.connections[j];
        if (connection.neighborIP === Constants.NONE) continue;

        const physicalConnectionKey = this.getPhysicalConnectionKey(
          device.ip,
          connection.selfPort,
          connection.neighborIP,
          connection.neighborPort
        );

        if (physicalConnections.has(physicalConnectionKey)) continue;
        physicalConnections.add(physicalConnectionKey);

        const pairKey = this.getConnectionPairKey(device.ip, connection.neighborIP);
        pairCount[pairKey] = (pairCount[pairKey] || 0) + 1;
      }
    }

    return pairCount;
  };

  getRedundantConnectionOffset = (connectionIndex, connectionTotal) => {
    if (connectionTotal <= 1) return 0;

    const spacing = 18;
    const middle = (connectionTotal + 1) / 2;
    return (connectionIndex - middle) * spacing;
  };

  getRedundantConnectionSmooth = (connectionIndex, connectionTotal) => {
    if (connectionTotal <= 1) return false;

    const visualOffset = this.getRedundantConnectionOffset(connectionIndex, connectionTotal);
    const roundness = Math.min(0.35, 0.12 + (Math.abs(visualOffset) / 130));

    return {
      enabled: true,
      type: visualOffset < 0 ? "curvedCCW" : "curvedCW",
      roundness: visualOffset === 0 ? 0 : roundness
    };
  };

  getNodeDisplayName = (nodeId) => {
    const node = nodes.get(nodeId);

    if (!node) {
      return nodeId;
    }

    if (node.manualDevice === true) {
      return node.tag || node.manualIP || node.label || nodeId;
    }

    return node.ip || node.label || nodeId;
  };

  createManualEdgeData = (edgeData) => {
    const edgeId = edgeData.id || ("manual-edge-" + Date.now() + "-" + Math.random().toString(36).slice(2, 8));

    return {
      id: edgeId,
      from: edgeData.from,
      to: edgeData.to,
      manualConnection: true,
      width: edgeData.width || 2.5,
      dashes: edgeData.dashes !== undefined ? edgeData.dashes : [2, 6],
      smooth: edgeData.smooth !== undefined ? edgeData.smooth : false,
      color: edgeData.color || {
        color: "#2F80FF",
        highlight: "#0057D9",
        hover: "#0057D9"
      },
      title: edgeData.title || (this.getNodeDisplayName(edgeData.from) + " <----> " + this.getNodeDisplayName(edgeData.to))
    };
  };

  restoreManualEdges = () => {
    this.state.savedNodePositions.forEach((savedEdge) => {
      if (savedEdge.manualConnection !== true) return;
      if (!savedEdge.from || !savedEdge.to) return;
      if (nodes.get(savedEdge.from) === null || nodes.get(savedEdge.to) === null) return;

      const manualEdge = this.createManualEdgeData(savedEdge);

      if (edges.get(manualEdge.id) !== null) return;

      edges.add(manualEdge);
    });
  };


  getNormalizedPortNumber = (portName) => {
    if (portName === undefined || portName === null) return "";

    const match = String(portName).match(/\d+/);
    if (!match) return String(portName).trim().toLowerCase();

    const parsedPort = parseInt(match[0], 10);
    return Number.isNaN(parsedPort) ? match[0] : String(parsedPort);
  };

  isSamePortName = (portA, portB) => {
    if (portA === undefined || portA === null || portB === undefined || portB === null) {
      return false;
    }

    if (String(portA) === String(portB)) {
      return true;
    }

    return this.getNormalizedPortNumber(portA) === this.getNormalizedPortNumber(portB);
  };



  // Draws each port number close to its device instead of placing the label in the center of the link.
  // Each unique physical connection receives its own labels. This is important for
  // redundant rings, where the same two devices can have two different port pairs.
  drawConnectionPortLabels = (ctx) => {
    if (!network || !edges || !nodes) return;

    const edgeList = edges.get();
    const drawnConnections = new Set();

    ctx.save();
    ctx.font = "bold 13px Arial";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillStyle = "#263238";

    edgeList.forEach((edge) => {
      if (!edge.from || !edge.to || edge.selfPortNumber === undefined || edge.neighborPortNumber === undefined) return;

      const connectionKey = edge.physicalConnectionKey || this.getPhysicalConnectionKey(
        edge.from,
        edge.selfPort || edge.selfPortNumber,
        edge.to,
        edge.neighborPort || edge.neighborPortNumber
      );

      if (drawnConnections.has(connectionKey)) return;
      drawnConnections.add(connectionKey);

      const positions = network.getPositions([edge.from, edge.to]);
      const fromPosition = positions[edge.from];
      const toPosition = positions[edge.to];
      if (!fromPosition || !toPosition) return;

      const dx = toPosition.x - fromPosition.x;
      const dy = toPosition.y - fromPosition.y;
      const length = Math.sqrt((dx * dx) + (dy * dy));
      if (length === 0) return;

      const ux = dx / length;
      const uy = dy / length;
      const px = -uy;
      const py = ux;
      const distanceFromDevice = 42;
      const sideOffset = 10 + (edge.labelOffset || 0);

      const labels = [
        {
          text: edge.selfPortNumber,
          x: fromPosition.x + (ux * distanceFromDevice) + (px * sideOffset),
          y: fromPosition.y + (uy * distanceFromDevice) + (py * sideOffset)
        },
        {
          text: edge.neighborPortNumber,
          x: toPosition.x - (ux * distanceFromDevice) + (px * sideOffset),
          y: toPosition.y - (uy * distanceFromDevice) + (py * sideOffset)
        }
      ];

      labels.forEach((label) => {
        ctx.fillText(label.text, label.x, label.y);
      });
    });

    ctx.restore();
  };

  // Creates the edges between the nodes in the graphic topology.
  // When two devices have more than one physical connection between them, the
  // connections are drawn as parallel/curved links to indicate a redundant ring.
  createEdges = () => {
    edges = new DataSet();

    const pairCount = this.buildConnectionPairCount();
    const pairIndex = {};
    const physicalConnections = new Set();

    for (let i = 0; i < this.state.topologyDevices.length; i++) {
      for (let j = 0; j < this.state.topologyDevices[i].connections.length; j++) {
        if (this.state.topologyDevices[i].connections[j].neighborIP !== Constants.NONE) {
          const selfIP = this.state.topologyDevices[i].ip;
          const selfPort = this.state.topologyDevices[i].connections[j].selfPort;
          const neighborPort = this.state.topologyDevices[i].connections[j].neighborPort;
          const neighborIP = this.state.topologyDevices[i].connections[j].neighborIP;
          const displaySelfPort = this.getPortNumberLabel(selfPort);
          const displayNeighborPort = this.getPortNumberLabel(neighborPort);
          const pairKey = this.getConnectionPairKey(selfIP, neighborIP);
          const physicalConnectionKey = this.getPhysicalConnectionKey(selfIP, selfPort, neighborIP, neighborPort);

          // Do not draw the same cable twice if the topology endpoint is reported
          // by both devices. Different port pairs between the same two IPs are kept.
          if (physicalConnections.has(physicalConnectionKey)) continue;
          physicalConnections.add(physicalConnectionKey);

          pairIndex[pairKey] = (pairIndex[pairKey] || 0) + 1;

          const connectionTotal = pairCount[pairKey] || 1;
          const connectionIndex = pairIndex[pairKey];
          const isRedundantConnection = connectionTotal > 1;
          const labelOffset = this.getRedundantConnectionOffset(connectionIndex, connectionTotal);

          edges.add({
            id: selfIP + "/"
              + selfPort + "/"
              + neighborIP + "/"
              + neighborPort,
            from: selfIP,
            to: neighborIP,
            selfPort: selfPort,
            neighborPort: neighborPort,
            selfPortNumber: displaySelfPort,
            neighborPortNumber: displayNeighborPort,
            physicalConnectionKey: physicalConnectionKey,
            isRedundantConnection: isRedundantConnection,
            labelOffset: labelOffset,
            width: isRedundantConnection ? 3.5 : 2.5,
            smooth: this.getRedundantConnectionSmooth(connectionIndex, connectionTotal),
            title:
              selfIP + ": " +
              " " + displaySelfPort + " <----> " +
              neighborIP + ":" +
              " " + displayNeighborPort
          });
        }
      }
    }

    this.restoreManualEdges();
    this.updateEdgesColors();
  };

  getManipulationOptions = () => {
    return {
      enabled: true,
      addEdge: this.addManualConnection,
      editEdge: false,
      deleteEdge: this.deleteManualConnections,
      initiallyActive: true,
      addNode: this.openAddManualDeviceModal,
      editNode: this.openEditManualDeviceModal,
      deleteNode: this.deleteManualDevices
    };
  };

  addManualConnection = (data, callback) => {
    if (!data || !data.from || !data.to || data.from === data.to) {
      alert(Strings.ALERT_SELECT_DIFFERENT_DEVICES_FOR_CONNECTION);
      callback(null);
      return;
    }

    if (nodes.get(data.from) === null || nodes.get(data.to) === null) {
      alert(Strings.ALERT_MANUAL_CONNECTION_CREATION_FAILED);
      callback(null);
      return;
    }

    callback(this.createManualEdgeData(data));
  };

  deleteManualConnections = (data, callback) => {
    const hasAutomaticConnection = data.edges.some((edgeId) => {
      const edge = edges.get(edgeId);
      return !edge || edge.manualConnection !== true;
    });

    if (hasAutomaticConnection) {
      alert(Strings.ALERT_ONLY_MANUAL_CONNECTIONS_CAN_BE_REMOVED);
      callback(null);
      return;
    }

    callback(data);
  };

  openAddManualDeviceModal = (data, callback) => {
    this.setState({
      isAddDeviceModalOpen: true,
      isEditingManualDevice: false,
      editingManualDeviceData: data,
      editingManualDeviceCallback: callback,
      deviceType: "",
      addDeviceTagInputValue: "",
      addDeviceIpInputValue: ""
    });
  };

  openEditManualDeviceModal = (data, callback) => {
    const currentNode = nodes.get(data.id);

    if (!currentNode || currentNode.manualDevice !== true) {
      alert(Strings.ALERT_ONLY_MANUAL_DEVICES_CAN_BE_EDITED);
      callback(null);
      return;
    }

    this.setState({
      isAddDeviceModalOpen: true,
      isEditingManualDevice: true,
      editingManualDeviceData: data,
      editingManualDeviceCallback: callback,
      deviceType: currentNode.deviceType || "",
      addDeviceTagInputValue: currentNode.tag || "",
      addDeviceIpInputValue: currentNode.manualIP || ""
    });
  };

  deleteManualDevices = (data, callback) => {
    const hasAutomaticDevice = data.nodes.some((nodeId) => {
      const node = nodes.get(nodeId);
      return !node || node.manualDevice !== true;
    });

    if (hasAutomaticDevice) {
      alert(Strings.ALERT_ONLY_MANUAL_DEVICES_CAN_BE_REMOVED);
      callback(null);
      return;
    }

    const hasAutomaticConnection = data.edges.some((edgeId) => {
      const edge = edges.get(edgeId);
      return edge && edge.manualConnection !== true;
    });

    if (hasAutomaticConnection) {
      alert(Strings.ALERT_AUTO_DETECTED_CONNECTIONS_CANNOT_BE_REMOVED);
      callback(null);
      return;
    }

    callback(data);
  };

  handleManualDeviceModalCancel = () => {
    const callback = this.state.editingManualDeviceCallback;

    if (callback) {
      callback(null);
    }

    this.closeAddDeviceModal();
  };

  handleManualDeviceModalConfirm = () => {
    const data = this.state.editingManualDeviceData;
    const callback = this.state.editingManualDeviceCallback;

    if (!data || !callback) return;

    if (this.state.isEditingManualDevice) {
      this.updateManualDevice(data, callback);
    }
    else {
      this.addDevice(data, callback);
    }
  };

  // If the X and Y positions of the devices are saved on the database
  // we disable the network physics
  createNetworkOptions = () => {
    let isDevicesPositionsSaved = this.hasSavedAutomaticNodePositions();

    if (!isDevicesPositionsSaved) {
      networkOptions = {
        autoResize: false,
        height: "100%",
        width: "100%",
        locale: "pt-br",
        locales: locales_pt_br,
        interaction: {
          hover: true
        },
        edges: {
          smooth: false,
          color: {
            color: "#2F80FF",
            highlight: "#0057D9",
            hover: "#0057D9"
          },
          font: {
            face: "Roboto",
            size: 13,
            color: "#263238",
            background: "#ffffff",
            strokeWidth: 3,
            strokeColor: "#ffffff",
            align: "top"
          }
        },
        nodes: {
          shapeProperties: {
            useImageSize: false
          },
          size: 34,
          font: {
            face: "Roboto",
            size: 16,
            color: "#263238",
            strokeWidth: 4,
            strokeColor: "#ffffff"
          }
        },
        physics: {
          enabled: false,
          stabilization: false
        },
        layout: {
          improvedLayout: false
        },
        manipulation: this.getManipulationOptions()
      };
    }
    else {
      networkOptions = {
        autoResize: false,
        height: "100%",
        width: "100%",
        locale: "pt-br",
        locales: locales_pt_br,
        interaction: {
          hover: true
        },
        edges: {
          smooth: false,
          color: {
            color: "#2F80FF",
            highlight: "#0057D9",
            hover: "#0057D9"
          },
          font: {
            face: "Roboto",
            size: 13,
            color: "#263238",
            background: "#ffffff",
            strokeWidth: 3,
            strokeColor: "#ffffff",
            align: "top"
          }
        },
        nodes: {
          shapeProperties: {
            useImageSize: false
          },
          size: 34,
          font: {
            face: "Roboto",
            size: 16,
            color: "#263238",
            strokeWidth: 4,
            strokeColor: "#ffffff"
          }
        },
        physics: {
          enabled: false,
          stabilization: false
        },
        layout: {
          improvedLayout: false
        },
        manipulation: this.getManipulationOptions()
      };
    }
  };

  captureTopologyCamera = () => {
    if (!network) return;

    this.lockedTopologyCamera = {
      scale: network.getScale(),
      position: network.getViewPosition()
    };
  };

  allowTopologyCameraChange = (duration = 700) => {
    this.cameraChangeAllowedUntil = Date.now() + duration;
  };

  keepTopologyCameraStable = () => {
    if (!network || !this.lockedTopologyCamera || this.isRestoringTopologyCamera) return;

    const cameraChangeIsAllowed = this.isUserCameraInteractionActive
      || Date.now() < this.cameraChangeAllowedUntil;

    if (cameraChangeIsAllowed) {
      this.captureTopologyCamera();
      return;
    }

    const currentScale = network.getScale();
    const currentPosition = network.getViewPosition();
    const lockedScale = this.lockedTopologyCamera.scale;
    const lockedPosition = this.lockedTopologyCamera.position;
    const scaleChanged = Math.abs(currentScale - lockedScale) > 0.000001;
    const positionChanged = Math.abs(currentPosition.x - lockedPosition.x) > 0.01
      || Math.abs(currentPosition.y - lockedPosition.y) > 0.01;

    if (!scaleChanged && !positionChanged) return;

    this.isRestoringTopologyCamera = true;
    network.moveTo({
      position: lockedPosition,
      scale: lockedScale,
      animation: false
    });
    this.isRestoringTopologyCamera = false;
  };

  handleTopologyCameraPointerDown = (event) => {
    if (event && event.target && event.target.tagName === "CANVAS") {
      this.isUserCameraInteractionActive = true;
    }
  };

  handleTopologyCameraPointerUp = () => {
    if (!this.isUserCameraInteractionActive) return;

    this.isUserCameraInteractionActive = false;
    this.captureTopologyCamera();
  };

  handleTopologyCameraWheel = () => {
    this.isUserCameraInteractionActive = true;

    if (this.cameraInteractionTimeout) {
      clearTimeout(this.cameraInteractionTimeout);
    }

    this.cameraInteractionTimeout = setTimeout(() => {
      this.isUserCameraInteractionActive = false;
      this.captureTopologyCamera();
    }, 180);
  };

  handleTopologyWindowResize = () => {
    if (this.topologyResizeTimeout) {
      clearTimeout(this.topologyResizeTimeout);
    }

    this.topologyResizeTimeout = setTimeout(() => {
      this.topologyResizeTimeout = null;

      if (!network || !network.body || !network.body.container) return;

      const currentScale = network.getScale();
      const currentPosition = network.getViewPosition();

      // Use percentages so vis-network follows the existing flex layout
      // without writing a fixed pixel height back into the container.
      network.setSize("100%", "100%");
      network.moveTo({
        position: currentPosition,
        scale: currentScale,
        animation: false
      });
      network.redraw();
      this.captureTopologyCamera();
    }, 100);
  };

  revealInitialTopologyView = (container) => {
    if (!network || !container || nodes.length === 0) return;

    const width = Math.round(container.clientWidth);
    const height = Math.round(container.clientHeight);

    if (width <= 0 || height <= 0) return;

    // Keep vis-network at the exact size already defined by the final CSS
    // layout. autoResize must remain disabled here; enabling it after fit()
    // causes a second canvas resize and the visible zoom reported by the user.
    network.setSize(`${width}px`, `${height}px`);
    network.redraw();
    network.fit({ animation: false });

    const fittedScale = network.getScale();
    const fittedCenter = network.getViewPosition();

    network.moveTo({
      position: fittedCenter,
      scale: fittedScale * INITIAL_TOPOLOGY_ZOOM_FACTOR,
      offset: { x: 0, y: INITIAL_TOPOLOGY_VERTICAL_OFFSET },
      animation: false
    });

    network.redraw();
    this.captureTopologyCamera();
    this.handleTopologyListDeviceSearch();

    // Reveal only after the final scale and position have been applied.
    // No requestAnimationFrame and no later automatic resize are used.
    container.style.visibility = "visible";
    container.style.opacity = "1";
  };

  createNetwork = () => {
    this.createNodes();
    this.createEdges();
    this.createNetworkOptions();

    let container = document.getElementById("network");

    let data = {
      nodes: nodes,
      edges: edges
    };

    if (network && network.destroy) {
      network.destroy();
    }

    // Hide the network before vis-network creates its first canvas. This is
    // intentionally inline so no CSS timing or selector specificity can expose
    // the default view before the final fitted position is ready.
    container.style.visibility = "hidden";
    container.style.opacity = "0";
    container.style.transition = "none";

    network = new Network(container, data, networkOptions);

    window.removeEventListener("resize", this.handleTopologyWindowResize);
    window.addEventListener("resize", this.handleTopologyWindowResize);

    container.removeEventListener("pointerdown", this.handleTopologyRegionMouseDown, true);
    container.removeEventListener("mousedown", this.handleTopologyRegionMouseDown, true);
    container.removeEventListener("mouseleave", this.handleTopologyRegionMouseLeave);
    container.removeEventListener("pointerdown", this.handleTopologyCameraPointerDown, true);
    container.removeEventListener("wheel", this.handleTopologyCameraWheel);
    window.removeEventListener("pointerup", this.handleTopologyCameraPointerUp);
    window.removeEventListener("mouseup", this.handleTopologyCameraPointerUp);
    window.removeEventListener("mousemove", this.handleTopologyRegionMouseMove);
    window.removeEventListener("pointermove", this.handleTopologyRegionMouseMove);
    window.removeEventListener("mouseup", this.handleTopologyRegionMouseUp);
    window.removeEventListener("pointerup", this.handleTopologyRegionMouseUp);
    container.addEventListener("pointerdown", this.handleTopologyRegionMouseDown, true);
    container.addEventListener("mousedown", this.handleTopologyRegionMouseDown, true);
    container.addEventListener("mouseleave", this.handleTopologyRegionMouseLeave);
    container.addEventListener("pointerdown", this.handleTopologyCameraPointerDown, true);
    container.addEventListener("wheel", this.handleTopologyCameraWheel, { passive: true });
    window.addEventListener("pointerup", this.handleTopologyCameraPointerUp);
    window.addEventListener("mouseup", this.handleTopologyCameraPointerUp);
    window.addEventListener("mousemove", this.handleTopologyRegionMouseMove);
    window.addEventListener("pointermove", this.handleTopologyRegionMouseMove);
    window.addEventListener("mouseup", this.handleTopologyRegionMouseUp);
    window.addEventListener("pointerup", this.handleTopologyRegionMouseUp);

    network.on("dragEnd", (params) => {
      if (!params.nodes || params.nodes.length === 0) return;

      network.storePositions();

      const updates = params.nodes.map((id) => {
        const node = nodes.get(id);
        return {
          id,
          x: this.snapToGrid(node.x),
          y: this.snapToGrid(node.y)
        };
      });

      nodes.update(updates);
    });

    network.on("beforeDrawing", (ctx) => {
      this.drawTopologyRegions(ctx);
    });

    network.on("afterDrawing", (ctx) => {
      this.drawConnectionPortLabels(ctx);
      this.drawSearchNodeHighlight(ctx);
      this.keepTopologyCameraStable();
      this.hideLoading();
    });

    const finalizeInitialView = () => this.revealInitialTopologyView(container);

    if (networkOptions.physics && networkOptions.physics.enabled) {
      // vis-network normally performs its own fit when stabilization finishes.
      // `fit: false` disables that behavior; this callback performs the only
      // initial fit, while the canvas is still hidden.
      network.once("stabilizationIterationsDone", finalizeInitialView);
    }
    else {
      finalizeInitialView();
    }

    network.on("click", (params) => {
      if (this.handleTopologyRegionCanvasClick(params)) {
        return;
      }

      if (params.nodes.length > 0) {
        const nodeId = params.nodes[0];
        const clickedNode = nodes.get(nodeId);

        if (clickedNode && clickedNode.manualDevice === true) {
          clearInterval(updateClickedDeviceSetInterval);
          this.setState({ activeTopologySidebarTab: "device" });

          // Manual devices do not have data in the backend.
          // Keep the side panel in the "no selected device" state and do not call deviceInfo/.
          this.setState({
            selectedDevice: {
              name: "",
              model: "",
              manufacturer: "",
              ip: "",
              mac: "",
              role: "",
              pingCurrent: "",
              pingMax: "",
              hasTsExpertEvents: ""
            }
          });
          return;
        }

        const device = this.state.liveList.find(d => d.ip === nodeId);
        if (device !== undefined) {
          this.onDeviceClick(device);
        }
      }
      else {
        this.setState({
          selectedDevice: {
            name: "",
            model: "",
            manufacturer: "",
            ip: "",
            mac: "",
            role: "",
            pingCurrent: "",
            pingMax: "",
            // Possui eventos no expert
            hasTsExpertEvents: "",
          }
        });
      }
    });

    // The initial automatic layout and every saved layout have stable positions,
    // so their live colors and links can be refreshed without moving nodes.
    if (nodes.length > 0) {
      clearInterval(updateDeviceColorsSetInterval);
      clearInterval(updateConnectionsSetInterval);
      updateDeviceColorsSetInterval = setInterval(this.updateDevicesColorStatus, Constants.GRAPHIC_TOPOLOGY_REFRESH_TIME);
      updateConnectionsSetInterval = setInterval(this.updateEdgesColors, Constants.GRAPHIC_TOPOLOGY_REFRESH_TIME);
    }

    // Finally verify if this page was opened from the network traffic page
    // from a click on a table cell of a device that has traffic errors increment
    this.handleTrafficIncrementDeviceSearch();
  };

  // Saves the position of each of the nodes so we can position them
  // on the same place when refreshing the page
  handleSaveNodesPositionClick = () => {
    this.snapAllNodesToGrid();

    let nodePositions = {
      graphicTopologyDevices: []
    };

    nodes.forEach((node) => {
      let nodePos = {
        id: node.id,
        ip: node.ip || node.id,
        x: node.x,
        y: node.y,
        image: node.image,
        label: node.label,
        shape: node.shape || "image",
        deviceType: node.deviceType || "",
        manualDevice: node.manualDevice === true,
        tag: node.tag || "",
        manualIP: node.manualIP || ""
      };
      nodePositions.graphicTopologyDevices.push(nodePos);
    });

    edges.forEach((edge) => {
      if (edge.manualConnection !== true) return;

      nodePositions.graphicTopologyDevices.push({
        id: edge.id,
        from: edge.from,
        to: edge.to,
        manualConnection: true,
        width: edge.width || 2.5,
        dashes: edge.dashes !== undefined ? edge.dashes : [2, 6],
        smooth: edge.smooth !== undefined ? edge.smooth : false,
        title: edge.title || Strings.GRAPHIC_TOPOLOGY_MANUAL_CONNECTION
      });
    });

    this.state.topologyRegions.forEach((region) => {
      nodePositions.graphicTopologyDevices.push(region);
    });

    let data = JSON.stringify(nodePositions);

    fetch(Constants.REQUEST_IP_ADDRESS + "graphicTopologySave_POST", {
      method: "POST",
      headers: {
        "Content-Type": "application/json;charset=UTF-8"
      },
      body: data
    })
      .then(UtilsInstance.checkStatus)
      .then(() => {
        alert(Strings.SAVE_TOPOLOGY_POS_SUCCESS_STRING);

        clearInterval(updateDeviceColorsSetInterval);
        clearInterval(updateConnectionsSetInterval);
        updateDeviceColorsSetInterval = setInterval(this.updateDevicesColorStatus, Constants.GRAPHIC_TOPOLOGY_REFRESH_TIME);
        updateConnectionsSetInterval = setInterval(this.updateEdgesColors, Constants.GRAPHIC_TOPOLOGY_REFRESH_TIME);
        this.setNetworkOptions(false);
      })
      .catch((error) => {
        alert(Strings.SAVE_TOPOLOGY_POS_FAILURE_STRING + error);
      });
  };

  // Deletes the positioning of the nodes in our database
  handleDeleteNodesPositionClick = () => {
    let data = JSON.stringify({
      reset: "True"
    });

    fetch(Constants.REQUEST_IP_ADDRESS + "graphicTopologyDrop_POST", {
      method: "POST",
      headers: {
        "Content-Type": "application/json;charset=UTF-8"
      },
      body: data
    }).then(UtilsInstance.checkStatus)
      .then(() => {
        alert(Strings.DROP_TOPOLOGY_POS_SUCCESS_STRING);

        clearInterval(updateDeviceColorsSetInterval);
        clearInterval(updateConnectionsSetInterval);
        updateDeviceColorsSetInterval = setInterval(this.updateDevicesColorStatus, Constants.GRAPHIC_TOPOLOGY_REFRESH_TIME);
        updateConnectionsSetInterval = setInterval(this.updateEdgesColors, Constants.GRAPHIC_TOPOLOGY_REFRESH_TIME);
        this.retrieveData();
      })
      .catch(function (error) {
        alert(Strings.DROP_TOPOLOGY_POS_FAILURE_STRING + error);
      });
  };

  // Pauses the network physics so the user can manually adjust the nodes
  // positioning
  handleEditNodesPositionClick = () => {
    clearInterval(updateDeviceColorsSetInterval);
    clearInterval(updateConnectionsSetInterval);
    this.setNetworkOptions(false);
  };

  updateDevicesColorStatus = () => {
    fetch(Constants.REQUEST_IP_ADDRESS, { mode: "cors" })
      .then(res => res.json())
      .then((data => this.setState({ liveList: data }, () => {
        for (let i = 0; i < this.state.liveList.length; i++) {
          if (nodes.get(this.state.liveList[i].ip) !== null) {
            let newImagePath = this.getDeviceImagePath(
              this.state.liveList[i].ip,
              this.state.liveList[i].status,
              this.state.liveList[i].role,
              this.state.liveList[i].isIgnoringMonitoring);
            // Only update the image if it is not the same as the old one
            // This saves so soooooooo much computational time
            const currentNode = nodes.get(this.state.liveList[i].ip);
            const topologyDevice = this.state.topologyDevices.find((device) => device.ip === this.state.liveList[i].ip) || currentNode;
            const updatedNode = {
              id: this.state.liveList[i].ip,
              label: this.getAutomaticDeviceLabel(topologyDevice),
              tag: this.getDeviceTagText(this.state.liveList[i].ip, topologyDevice),
              profname: this.getDeviceProfnameText(topologyDevice)
            };

            if (currentNode.image !== newImagePath) {
              updatedNode.image = newImagePath;
            }

            nodes.update(updatedNode);
          }
        }
      })))
      .catch(() => {
        this.setState({ liveList: [] });
      });
  };

  // Updates automatic connection colors using only traffic errors available in the Standard product.
  // Orange indicates input/output traffic errors on either endpoint; otherwise the link stays blue.
  updateEdgesColors = async () => {
    const normalColor = {
      color: "#2F80FF",
      highlight: "#0057D9",
      hover: "#0057D9"
    };
    const errorColor = {
      color: "#ff6600",
      highlight: "#ff6600",
      hover: "#ff6600"
    };

    const getNumericValue = (value) => {
      const parsedValue = Number(value);
      return Number.isFinite(parsedValue) ? parsedValue : 0;
    };

    const findTrafficPort = (trafficDevices, deviceIP, portName) => {
      const device = trafficDevices.find((item) => item.ip === deviceIP);
      if (!device || !Array.isArray(device.ports)) return null;

      return device.ports.find((port) =>
        this.isSamePortName(port.topologyName, portName)
        || this.isSamePortName(port.ownPortID, portName)
        || this.isSamePortName(port.portName, portName)
        || this.isSamePortName(port.name, portName)
      ) || null;
    };

    const portHasTrafficError = (port) => port !== null && (
      getNumericValue(port.exitErrorsIncrement) > 0
      || getNumericValue(port.inputErrorsIncrement) > 0
    );

    try {
      const trafficResponse = await fetch(Constants.REQUEST_IP_ADDRESS + "traffic", { mode: "cors" })
        .then(res => res.json())
        .catch(() => ({ devices: [] }));

      const trafficDevices = Array.isArray(trafficResponse)
        ? trafficResponse
        : (trafficResponse.devices || []);

      this.setState({ traffic: trafficDevices });

      const automaticEdges = edges.get({
        filter: edge => edge.manualConnection !== true
      });

      automaticEdges.forEach((edge) => {
        const edgeIDParts = String(edge.id).split("/");
        if (edgeIDParts.length < 4) return;

        const selfIP = edgeIDParts[0];
        const selfPort = edgeIDParts[1];
        const neighborIP = edgeIDParts[2];
        const neighborPort = edgeIDParts[3];

        const selfTrafficPort = findTrafficPort(trafficDevices, selfIP, selfPort);
        const neighborTrafficPort = findTrafficPort(trafficDevices, neighborIP, neighborPort);
        const hasTrafficError = portHasTrafficError(selfTrafficPort)
          || portHasTrafficError(neighborTrafficPort);

        edges.update({
          id: edge.id,
          color: hasTrafficError ? errorColor : normalColor
        });
      });
    } catch (error) {
      console.log(error);
      this.setState({ traffic: [] });
    }
  };

  updateSelectedDevice = async () => {
    if (this.state.selectedDevice.manualDevice === true || this.state.selectedDevice.mac === "-" || this.state.selectedDevice.mac === "manual-device") {
      return;
    }

    if (this.state.selectedDevice.mac !== "") {

      let res = await fetch(Constants.REQUEST_IP_ADDRESS + "deviceInfo/" + this.state.selectedDevice.mac, { mode: "cors" })
        .then(UtilsInstance.checkStatus)
        .then(res => res.json())
        .then(data => {
          return {
            deviceInfo: data,
            success: true
          };
        })
        .catch((error) => {
          console.log(error);
          return {
            deviceInfo: {},
            success: false
          };
        });

      if (res.success) {
        this.setState({ selectedDevice: res.deviceInfo });
      }
    }
  };

  setNetworkOptions = (isPhysicsOn) => {
    if (!network) return;

    // Updating physics or manipulation options must never change the camera.
    // Keep the exact view selected by the user and leave automatic canvas
    // resizing disabled, otherwise vis-network may recalculate the scale.
    const currentScale = network.getScale();
    const currentPosition = network.getViewPosition();

    if (isPhysicsOn) {
      network.setOptions(
        {
          autoResize: false,
          height: "100%",
          width: "100%",
          locale: "pt-br",
          locales: locales_pt_br,
          interaction: {
            hover: true
          },
          edges: {
            smooth: false,
            color: {
              color: "#2F80FF",
              highlight: "#0057D9",
              hover: "#0057D9"
            },
            font: {
              face: "Roboto",
              size: 13,
              color: "#263238",
              background: "#ffffff",
              strokeWidth: 3,
              strokeColor: "#ffffff",
              align: "top"
            }
          },
          physics: {
            enabled: true,
            stabilization: {
              enabled: true,
              fit: false
            }
          },
          layout: {
            improvedLayout: false
          },
          manipulation: this.getManipulationOptions()
        }
      );
    }
    else {
      network.setOptions(
        {
          autoResize: false,
          height: "100%",
          width: "100%",
          locale: "pt-br",
          locales: locales_pt_br,
          interaction: {
            hover: true
          },
          edges: {
            smooth: false,
            color: {
              color: "#2F80FF",
              highlight: "#0057D9",
              hover: "#0057D9"
            },
            font: {
              face: "Roboto",
              size: 13,
              color: "#263238",
              background: "#ffffff",
              strokeWidth: 3,
              strokeColor: "#ffffff",
              align: "top"
            }
          },
          physics: {
            enabled: false,
            stabilization: false
          },
          layout: {
            improvedLayout: false
          },
          manipulation: this.getManipulationOptions()
        }
      );
    }

    network.moveTo({
      position: currentPosition,
      scale: currentScale,
      animation: false
    });
    this.captureTopologyCamera();
    network.redraw();
  };

  // Focus a device selected from the tabular topology view.
  handleTopologyListDeviceSearch = () => {
    const routeState = this.props.location && this.props.location.state;

    if (!routeState || routeState.source !== "topology-list" || !routeState.deviceIP || !network) {
      return;
    }

    const deviceIP = routeState.deviceIP;

    if (this.focusedTopologyListDeviceIP === deviceIP) {
      return;
    }

    const selectedNode = nodes.get(deviceIP);

    if (!selectedNode) {
      return;
    }

    this.focusedTopologyListDeviceIP = deviceIP;
    this.allowTopologyCameraChange(900);
    network.selectNodes([deviceIP]);
    network.focus(deviceIP, {
      scale: Math.max(network.getScale(), 1),
      animation: {
        duration: 550,
        easingFunction: "easeInOutQuad"
      }
    });

    const selectedDevice = this.state.liveList.find(device => device.ip === deviceIP);
    if (selectedDevice !== undefined) {
      this.onDeviceClick(selectedDevice);
    }
  };

  // Search for the device that has traffic errors increment on one of its ports
  handleTrafficIncrementDeviceSearch = () => {
    if (this.props.location.state && this.props.location.state.source === "topology-list") {
      return;
    }
    if (this.props.location.state !== undefined && this.props.location.state !== null) {
      if (this.props.location.state.deviceIP !== undefined && this.props.location.state !== null) {
        // Only try to search if it is a valid IP
        if (ipRegex.test(this.props.location.state.deviceIP)) {
          let deviceIP = this.props.location.state.deviceIP;
          for (let i = 0; i < this.state.devicesThatHaveAConnection.length; i++) {
            if (deviceIP === this.state.devicesThatHaveAConnection[i]) {

              if (this.isPortBeingUsed(deviceIP, this.props.location.state.portName)) {
                this.allowTopologyCameraChange(900);
                network.focus(deviceIP);
                network.selectNodes([deviceIP]);
                network.fit({
                  nodes: [deviceIP],
                  animation: true
                });
              }
              else {
                // Show the user that the port has no active connections
                alert(Strings.DISCONNECTED_PORT_1 + this.props.location.state.portName + Strings.DISCONNECTED_PORT_2 + deviceIP + Strings.DISCONNECTED_PORT_3);

                // And zoom in to show the device
                this.allowTopologyCameraChange(900);
                network.focus(deviceIP);
                network.selectNodes([deviceIP]);
                network.fit({
                  nodes: [deviceIP],
                  animation: true
                });
              }
              return;
            }
          }
          alert(Strings.FORM_INPUT_NOT_FOUND_1 + deviceIP + Strings.FORM_INPUT_NOT_FOUND_2);

          return;
        }
        else {
          alert(Strings.FORM_INPUT_INVALID_IP);
        }
      }
    }
  };

  // Returns true if the 'port' of the device with 'deviceIP' has an active connection, false otherwise
  isPortBeingUsed = (deviceIP, port) => {
    for (let i = 0; i < this.state.topologyDevices.length; i++) {
      if (this.state.topologyDevices[i].ip === deviceIP) {
        for (let j = 0; j < this.state.topologyDevices[i].connections.length; j++) {
          if (this.state.topologyDevices[i].connections[j].selfPort === port) {
            return true;
          }
        }
      }
    }
    return false;
  };

  // ----------------- Image Export -----------------
  exportSvg = async () => {
    let isTopologySaved = await this.isTopologyIsSaved();

    if (isTopologySaved) {
      let networkContainer = network.body.container;
      let ctx = new C2S({ width: networkContainer.clientWidth, height: networkContainer.clientHeight, enableMirroring: true });

      let canvasProto = network.canvas.__proto__;
      let currentGetContext = canvasProto.getContext;
      canvasProto.getContext = function () {
        return ctx;
      };
      let svgOptions = {
        nodes: {
          shapeProperties: {
            interpolation: false //so images are not scaled svg will get full image
          },
          scaling: { label: { drawThreshold: 0 } }
        },
        edges: {
          scaling: { label: { drawThreshold: 0 } }
        }
      };
      network.setOptions(svgOptions);
      canvasProto.getContext = currentGetContext;
      setTimeout(() => {
        let svg = ctx.getSerializedSvg();
        this.showSvg(svg);
      }, 1000);
    }
    else {
      this.setState({ isSaveTopologyModalOpen: true });
    }
  };

  isTopologyIsSaved = async () => {
    let savedDevices = await fetch(Constants.REQUEST_IP_ADDRESS + "graphicTopology", { mode: "cors" })
      .then(res => res.json())
      .then(data => data)
      .catch(() => {
        return [];
      });

    return (savedDevices.length > 0);
  };

  handleSaveTopologyModalButtonClick = () => {
    this.setState({ isSaveTopologyModalOpen: false });
  };

  showSvg = (svg) => {
    let svgBlob = new Blob([svg], { type: "image/svg+xml" });
    this.openBlob(svgBlob, "network.svg");
  };


  openBlob = (blob, fileName) => {
    if (window.navigator && window.navigator.msSaveOrOpenBlob) {

      //blobToDataURL(blob, function(dataurl){window.open(dataurl);});
      window.navigator.msSaveOrOpenBlob(blob, fileName);
    }
    else {
      let a = document.getElementById("blobLink");
      if (!a) {
        a = document.createElement("a");
        document.body.appendChild(a);
        a.setAttribute("id", "blobLink");
        a.style = "display: none";
      }
      let data = window.URL.createObjectURL(blob);
      a.href = data;
      a.download = fileName;
      a.click();
      setTimeout(function () {
        // For Firefox it is necessary to delay revoking the ObjectURL
        window.URL.revokeObjectURL(data);
      }, 100);
    }
  };

  // ----------------- Topology Import / Export -----------------
  exportTopology = () => {
    if (network !== undefined && network !== null) {
      this.snapAllNodesToGrid();

      let nodePositions = {
        graphicTopologyDevices: []
      };

      nodes.forEach((node) => {
        let nodePos = {
          id: node.id,
          ip: node.ip || node.id,
          x: node.x,
          y: node.y,
          image: node.image,
          label: node.label,
          shape: node.shape || "image",
          deviceType: node.deviceType || "",
          manualDevice: node.manualDevice === true,
          tag: node.tag || "",
          manualIP: node.manualIP || ""
        };
        nodePositions.graphicTopologyDevices.push(nodePos);
      });

      edges.forEach((edge) => {
        if (edge.manualConnection !== true) return;

        nodePositions.graphicTopologyDevices.push({
          id: edge.id,
          from: edge.from,
          to: edge.to,
          manualConnection: true,
          width: edge.width || 2.5,
          dashes: edge.dashes !== undefined ? edge.dashes : [2, 6],
          smooth: edge.smooth !== undefined ? edge.smooth : false,
          title: edge.title || Strings.GRAPHIC_TOPOLOGY_MANUAL_CONNECTION
        });
      });

      this.state.topologyRegions.forEach((region) => {
        nodePositions.graphicTopologyDevices.push(region);
      });

      let data = JSON.stringify(nodePositions);

      let dataStr = "data:application/json;charset=utf-8," + encodeURIComponent(data);
      let downloadAnchorNode = document.createElement("a");
      downloadAnchorNode.setAttribute("href", dataStr);
      downloadAnchorNode.setAttribute("download", Strings.PROFINET_TOPOLOGY_JSON);
      document.body.appendChild(downloadAnchorNode); // required for firefox
      downloadAnchorNode.click();
      downloadAnchorNode.remove();
    }
  };

  importTopology = () => {
    let fileInput = document.getElementById("import-topology-input-file");

    if (fileInput.files[0] !== undefined && fileInput.files[0] !== null) {
      let file = fileInput.files[0];
      let fileReader = new FileReader();

      // Import the topology information when the file is ready to be read
      fileReader.onload = (event) => {
        try {
          let topologyJSONObject = JSON.parse(event.target.result);

          // Check if saved devices or connections are not defined
          let areDevicesOk = topologyJSONObject.graphicTopologyDevices !== undefined && topologyJSONObject.graphicTopologyDevices !== null;

          if (areDevicesOk) {
            // If there are no problems, save them
            fetch(Constants.REQUEST_IP_ADDRESS + "graphicTopologySave_POST", {
              method: "POST",
              headers: {
                "Content-Type": "application/json;charset=UTF-8"
              },
              body: JSON.stringify({ graphicTopologyDevices: topologyJSONObject.graphicTopologyDevices })
            })
              .then(UtilsInstance.checkStatus)
              .then(() => {
                alert(Strings.TOPOLOGY_IMPORT_SUCCESS);

                // If everything was ok, retrieve data to refresh the network
                this.retrieveData();
              })
              .catch((error) => {
                alert(Strings.ERROR_IMPORTING_GRAPHIC_TOPOLOGY + error);
              });
          }
          else {
            alert(Strings.ERROR_IMPORTING_GRAPHIC_TOPOLOGY + Strings.INVALID_OR_CORRUPT_FILE);
          }
        }
        catch (error) {
          alert(Strings.ERROR_IMPORTING_GRAPHIC_TOPOLOGY + Strings.INVALID_OR_CORRUPT_FILE);
        }
      };

      fileReader.readAsText(file);
    }
    else {
      alert(Strings.PLEASE_SELECT_TOPOLOGY_FILE);
    }
  };

  // ----------------- Add / Remove devices -----------------
  onTypeDropdownChange = (e, { value }) => {
    this.setState({ deviceType: value });
  };

  onChangeDeviceInput = (e, data) => {
    if (data.value.length <= 20) {
      this.setState({
        addDeviceTagInputValue: data.value
      });
    }
  };

  onChangeDeviceIpInput = (e, data) => {
    if (data.value.length <= 15) {
      this.setState({
        addDeviceIpInputValue: data.value
      });
    }
  };

  closeAddDeviceModal = () => {
    this.setState({
      isAddDeviceModalOpen: false,
      isEditingManualDevice: false,
      editingManualDeviceData: null,
      editingManualDeviceCallback: null,
      deviceType: "",
      addDeviceTagInputValue: "",
      addDeviceIpInputValue: ""
    });
  };

  updateManualDevice = async (data, callback) => {
    if (this.state.deviceType === "") {
      alert(Strings.ALERT_CHOOSE_DEVICE_TYPE);
    }
    else {
      const isTagInputEmpty = this.state.addDeviceTagInputValue === "";
      const isIpEmpty = this.state.addDeviceIpInputValue === "";
      const isIpValid = UtilsInstance.validateIPAddress(this.state.addDeviceIpInputValue);

      if (isTagInputEmpty) {
        console.log("Tag em branco");
      }
      else if (!isIpEmpty && !isIpValid) {
        console.log("IP invÃ¡lido");
      }
      else {
        let imagePath = this.getDeviceImagePathBasedOnDeviceType(this.state.deviceType);
        let labelString = this.getDeviceLabelStringBasedOnType(this.state.deviceType);

        data.label = labelString;
        data.shape = "image";
        data.image = imagePath;
        data.deviceType = this.state.deviceType;
        data.manualDevice = true;
        data.tag = this.state.addDeviceTagInputValue;
        data.manualIP = this.state.addDeviceIpInputValue;
        data.ip = this.state.addDeviceIpInputValue || data.id;
        data.shapeProperties = {
          useImageSize: false
        };
        data.size = 34;
        data.font = {
          size: 16,
          color: "#263238",
          strokeWidth: 4,
          strokeColor: "#ffffff"
        };
        data.x = this.snapToGrid(data.x);
        data.y = this.snapToGrid(data.y);

        this.setState({
          isAddDeviceModalOpen: false,
          isEditingManualDevice: false,
          editingManualDeviceData: null,
          editingManualDeviceCallback: null,
          deviceType: "",
          addDeviceTagInputValue: "",
          addDeviceIpInputValue: ""
        });

        callback(data);
      }
    }
  };

  addDevice = async (data, callback) => {
    if (this.state.deviceType === "") {
      alert(Strings.ALERT_CHOOSE_DEVICE_TYPE);

    }
    else {
      const isTagInputEmpty = this.state.addDeviceTagInputValue === "";
      const isIpEmpty = this.state.addDeviceIpInputValue === "";
      const isIpValid = UtilsInstance.validateIPAddress(this.state.addDeviceIpInputValue);

      // Se a tag digitada Ã© um espaÃ§o em branco mostrar na tela que a tag nÃ£o pode ser em branco
      if (isTagInputEmpty) {
        console.log("Tag em branco");
      }
      // Se digitaram algo no input de IPm verificar se Ã© um IP de verdade
      // se o IP nÃ£o Ã© vÃ¡lido mostrar na tela que o IP Ã© invÃ¡lido
      else if (!isIpEmpty && !isIpValid) {
        console.log("IP invÃ¡lido");
      }
      // Se a tag estiver preenchida, e o IP estiver em branco, ou for um IP vÃ¡lido, continue
      else {
        let imagePath = this.getDeviceImagePathBasedOnDeviceType(this.state.deviceType);
        let labelString = this.getDeviceLabelStringBasedOnType(this.state.deviceType);

        data.id = "manual/" + this.state.deviceType + "/" + Date.now();
        data.label = labelString;
        data.shape = "image";
        data.image = imagePath;
        data.deviceType = this.state.deviceType;
        data.manualDevice = true;
        data.tag = this.state.addDeviceTagInputValue;
        data.manualIP = this.state.addDeviceIpInputValue;
        data.ip = this.state.addDeviceIpInputValue || data.id;
        data.shapeProperties = {
          useImageSize: false
        };
        data.size = 34;
        data.font = {
          size: 16,
          color: "#263238",
          strokeWidth: 4,
          strokeColor: "#ffffff"
        };
        data.x = this.snapToGrid(data.x);
        data.y = this.snapToGrid(data.y);
        this.setState({
          isAddDeviceModalOpen: false,
          isEditingManualDevice: false,
          editingManualDeviceData: null,
          editingManualDeviceCallback: null,
          deviceType: "",
          addDeviceTagInputValue: "",
          addDeviceIpInputValue: ""
        });
        callback(data);
      }
    }
  };

  // Returns the image path corresponding to a device type
  getDeviceImagePathBasedOnDeviceType = (deviceType) => {
    if (deviceType === SWITCH_DEVICE_TYPE) {
      return Constants.SWITCH_IMG_PATH;
    }
    else if (deviceType === TAP_DEVICE_TYPE) {
      return Constants.TAP_IMG_PATH;
    }
    else if (deviceType === PC_DEVICE_TYPE) {
      return Constants.PC_IMG_PATH;
    }
    else if (deviceType === SERVER_DEVICE_TYPE) {
      return Constants.SERVER_IMG_PATH;
    }
    else if (deviceType === ACCESS_POINT_TYPE) {
      return Constants.ACCESS_POINT_IMG_PATH;
    }
    else if (deviceType === HMI_TYPE) {
      return Constants.HMI_IMG_PATH;
    }
    else if (deviceType === ENDPOINT_TYPE) {
      return Constants.ENDPOINT_IMG_PATH;
    }
    else if (deviceType === FIREWALL_TYPE) {
      return Constants.FIREWALL_IMG_PATH;
    }
    else {
      return Constants.PC_IMG_PATH;
    }
  };

  getDeviceLabelStringBasedOnType = (deviceType) => {
    let labelString = "";

    if (deviceType === SWITCH_DEVICE_TYPE) {
      labelString = document.getElementById(ADD_DEVICE_TAG_ID).value + "\n" + this.state.addDeviceIpInputValue + "\n" + Strings.SWITCH;
    }
    else if (deviceType === TAP_DEVICE_TYPE) {
      labelString = document.getElementById(ADD_DEVICE_TAG_ID).value + "\n" + this.state.addDeviceIpInputValue + "\n" + Strings.TAP;
    }
    else if (deviceType === PC_DEVICE_TYPE) {
      labelString = document.getElementById(ADD_DEVICE_TAG_ID).value + "\n" + this.state.addDeviceIpInputValue + "\n" + Strings.PC;
    }
    else if (deviceType === SERVER_DEVICE_TYPE) {
      labelString = document.getElementById(ADD_DEVICE_TAG_ID).value + "\n" + this.state.addDeviceIpInputValue + "\n" + Strings.SERVER;
    }
    else if (deviceType === ACCESS_POINT_TYPE) {
      labelString = document.getElementById(ADD_DEVICE_TAG_ID).value + "\n" + this.state.addDeviceIpInputValue + "\n" + Strings.ACCESS_POINT;
    }
    else if (deviceType === HMI_TYPE) {
      labelString = document.getElementById(ADD_DEVICE_TAG_ID).value + "\n" + this.state.addDeviceIpInputValue + "\n" + Strings.HMI;
    }
    else if (deviceType === FIREWALL_TYPE) {
      labelString = document.getElementById(ADD_DEVICE_TAG_ID).value + "\n" + this.state.addDeviceIpInputValue + "\n" + Strings.FIREWALL;
    }
    else if (deviceType === ENDPOINT_TYPE) {
      labelString = document.getElementById(ADD_DEVICE_TAG_ID).value + "\n" + this.state.addDeviceIpInputValue + "\n" + Strings.ENDPOINT;
    }

    return labelString;
  };

  // ----------------- Lifecycle Methods ----------------
  componentDidMount() {
    sessionStorage.removeItem("auth");
    this.retrieveGraphicTopologyDisplayConfig();
    this.retrieveData();
  }

  componentWillUnmount() {
    clearInterval(updateDeviceColorsSetInterval);
    clearInterval(updateConnectionsSetInterval);
    clearInterval(updateClickedDeviceSetInterval);
    clearTimeout(this.cameraInteractionTimeout);
    clearTimeout(this.topologyResizeTimeout);
    window.removeEventListener("resize", this.handleTopologyWindowResize);
    this.stopSearchNodeHighlight();

    const container = document.getElementById("network");
    if (container) {
      container.removeEventListener("pointerdown", this.handleTopologyRegionMouseDown, true);
      container.removeEventListener("mousedown", this.handleTopologyRegionMouseDown, true);
      container.removeEventListener("mouseleave", this.handleTopologyRegionMouseLeave);
      container.removeEventListener("pointerdown", this.handleTopologyCameraPointerDown, true);
      container.removeEventListener("wheel", this.handleTopologyCameraWheel);
    }

    window.removeEventListener("mousemove", this.handleTopologyRegionMouseMove);
    window.removeEventListener("pointermove", this.handleTopologyRegionMouseMove);
    window.removeEventListener("mouseup", this.handleTopologyRegionMouseUp);
    window.removeEventListener("pointerup", this.handleTopologyRegionMouseUp);
    window.removeEventListener("pointerup", this.handleTopologyCameraPointerUp);
    window.removeEventListener("mouseup", this.handleTopologyCameraPointerUp);

    if (network && network.destroy) {
      network.destroy();
      network = undefined;
    }
  }

  getTopologyDeviceCount = () => {
    if (!nodes) return 0;

    let automaticDeviceCount = 0;

    nodes.forEach((node) => {
      if (node.manualDevice !== true) {
        automaticDeviceCount++;
      }
    });

    return automaticDeviceCount;
  };

  getManualDeviceCount = () => {
    if (!nodes) return 0;

    let manualDeviceCount = 0;
    nodes.forEach((node) => {
      if (node.manualDevice === true) manualDeviceCount++;
    });

    return manualDeviceCount;
  };

  renderTopologySidebarStats = () => (
    <div className="ts-gt-sidebar-stats">
      <div className="ts-gt-stat-card">
        <Icon name="sitemap" />
        <span>{this.getTopologyDeviceCount()}</span>
        <small>{Strings.GRAPHIC_TOPOLOGY_DEVICES}</small>
      </div>
      <div className="ts-gt-stat-card">
        <Icon name="object group outline" />
        <span>{this.state.topologyRegions.length}</span>
        <small>{Strings.GRAPHIC_TOPOLOGY_REGIONS}</small>
      </div>
      <div className="ts-gt-stat-card">
        <Icon name="plus square outline" />
        <span>{this.getManualDeviceCount()}</span>
        <small>{Strings.GRAPHIC_TOPOLOGY_MANUAL_DEVICES}</small>
      </div>
    </div>
  );

  setActiveTopologySidebarTab = (activeTopologySidebarTab) => {
    this.setState({ activeTopologySidebarTab });
  };

  getTopologySidebarTabClass = (tab) => {
    return this.state.activeTopologySidebarTab === tab
      ? "ts-gt-dock-tab active"
      : "ts-gt-dock-tab";
  };

  renderTopologySidebarTabs = () => (
    <div className="ts-gt-dock-tabs">
      <button
        type="button"
        className={this.getTopologySidebarTabClass("position")}
        onClick={() => this.setActiveTopologySidebarTab("position")}>
        <Icon name="crosshairs" />
        <span>{Strings.GRAPHIC_TOPOLOGY_POSITION_TAB}</span>
      </button>
      <button
        type="button"
        className={this.getTopologySidebarTabClass("display")}
        onClick={() => this.setActiveTopologySidebarTab("display")}>
        <Icon name="eye" />
        <span>{Strings.GRAPHIC_TOPOLOGY_DISPLAY_TAB}</span>
      </button>
      <button
        type="button"
        className={this.getTopologySidebarTabClass("regions")}
        onClick={() => this.setActiveTopologySidebarTab("regions")}>
        <Icon name="object group outline" />
        <span>{Strings.GRAPHIC_TOPOLOGY_REGIONS_TAB}</span>
      </button>
      <button
        type="button"
        className={this.getTopologySidebarTabClass("export")}
        onClick={() => this.setActiveTopologySidebarTab("export")}>
        <Icon name="download" />
        <span>{Strings.GRAPHIC_TOPOLOGY_FILES_TAB}</span>
      </button>
      <button
        type="button"
        className={this.getTopologySidebarTabClass("device")}
        onClick={() => this.setActiveTopologySidebarTab("device")}>
        <Icon name="hdd outline" />
        <span>{Strings.GRAPHIC_TOPOLOGY_DEVICE_TAB}</span>
      </button>
    </div>
  );

  renderTopologySidebarContent = () => {
    if (this.state.activeTopologySidebarTab === "display") {
      return (
        <Segment className="ts-gt-panel ts-gt-dock-panel">
          <div className="ts-gt-panel-title">
            <Icon name="eye" />
            <span>{Strings.GRAPHIC_TOPOLOGY_DISPLAY_TAB}</span>
          </div>
          <p className="ts-gt-panel-help">{Strings.GRAPHIC_TOPOLOGY_DISPLAY_HELP}</p>
          <Form style={topologyDisplayOptionsStyle}>
            <Form.Checkbox
              label={Strings.GRAPHIC_TOPOLOGY_SHOW_IP}
              checked={this.state.showDeviceIpOnTopology}
              disabled={this.state.isTopologyDisplayConfigLoading || this.state.isTopologyDisplayConfigSaving}
              onChange={this.handleShowDeviceIpOnTopologyChange} />
            <Form.Checkbox
              label={Strings.GRAPHIC_TOPOLOGY_SHOW_TAG}
              checked={this.state.showDeviceTagOnTopology}
              disabled={this.state.isTopologyDisplayConfigLoading || this.state.isTopologyDisplayConfigSaving}
              onChange={this.handleShowDeviceTagOnTopologyChange} />
            <Form.Checkbox
              label={Strings.GRAPHIC_TOPOLOGY_SHOW_PROFINAME}
              checked={this.state.showDeviceProfnameOnTopology}
              disabled={this.state.isTopologyDisplayConfigLoading || this.state.isTopologyDisplayConfigSaving}
              onChange={this.handleShowDeviceProfnameOnTopologyChange} />
            <Button
              className="ts-gt-action-button"
              type="button"
              size="small"
              compact
              fluid
              color="teal"
              loading={this.state.isTopologyDisplayConfigSaving}
              disabled={this.state.isTopologyDisplayConfigLoading || this.state.isTopologyDisplayConfigSaving}
              onClick={this.saveGraphicTopologyDisplayConfig}>
              {Strings.SAVE}
            </Button>
          </Form>
        </Segment>
      );
    }

    if (this.state.activeTopologySidebarTab === "regions") {
      return (
        <Segment className="ts-gt-panel ts-gt-dock-panel">
          <div className="ts-gt-panel-title">
            <Icon name="object group outline" />
            <span>{Strings.GRAPHIC_TOPOLOGY_REGIONS_TAB}</span>
          </div>
          <p className="ts-gt-panel-help">{Strings.GRAPHIC_TOPOLOGY_REGIONS_HELP}</p>
          <Dropdown
            className="ts-gt-region-dropdown"
            placeholder={Strings.GRAPHIC_TOPOLOGY_SELECTED_REGION_PLACEHOLDER}
            fluid
            selection
            clearable
            options={this.getTopologyRegionOptions()}
            value={this.state.selectedTopologyRegionId}
            onChange={this.onTopologyRegionDropdownChange} />
          <div className="ts-gt-button-stack">
            <Button className="ts-gt-action-button" size="small" compact fluid color="teal" onClick={this.handleAddTopologyRegionClick}>{Strings.GRAPHIC_TOPOLOGY_ADD_REGION}</Button>
            <Button className="ts-gt-action-button" size="small" compact fluid color="teal" onClick={this.handleEditTopologyRegionClick}>{Strings.GRAPHIC_TOPOLOGY_EDIT_REGION}</Button>
            <Button className="ts-gt-action-button" size="small" compact fluid color="teal" onClick={this.handleDeleteTopologyRegionClick}>{Strings.GRAPHIC_TOPOLOGY_REMOVE_REGION}</Button>
            {this.state.isAddingTopologyRegion && (
              <Button className="ts-gt-action-button ts-gt-cancel-button" size="small" compact fluid color="grey" onClick={this.handleCancelTopologyRegionClick}>{Strings.GRAPHIC_TOPOLOGY_CANCEL_REGION_DRAWING}</Button>
            )}
          </div>
        </Segment>
      );
    }

    if (this.state.activeTopologySidebarTab === "export") {
      return (
        <Segment className="ts-gt-panel ts-gt-dock-panel">
          <div className="ts-gt-panel-title">
            <Icon name="download" />
            <span>{Strings.GRAPHIC_TOPOLOGY_FILES_TAB}</span>
            <Popup position='bottom right' hoverable pinned wide="very"
              trigger={<Icon name="question circle" color="teal" style={{ marginLeft: "4px", marginRight: 0, cursor: "pointer" }} />}>
              <Popup.Content>
                <p><b>{Strings.PS}</b> {Strings.EXPORT_SVG_POPUP_MSG_1}</p>
                <p><b>{Strings.PS_2}</b> {Strings.EXPORT_SVG_POPUP_MSG_3}</p>
                <Image src={graphicTopologyInImg} size="medium" />
                <p><b>{Strings.PS_3}</b> {Strings.EXPORT_SVG_POPUP_MSG_4}</p>
                <Image src={graphicTopologyOutImg} size="medium" />
              </Popup.Content>
            </Popup >
          </div>
          <p className="ts-gt-panel-help">{Strings.GRAPHIC_TOPOLOGY_FILES_HELP}</p>
          <div className="ts-gt-button-stack">
            <Button className="ts-gt-action-button" size="small" compact fluid color="teal" onClick={this.exportSvg}>{Strings.EXPORT_SVG}</Button>
            <Button className="ts-gt-action-button" size="small" compact fluid color="teal" onClick={this.exportTopology}>{Strings.EXPORT_TOPOLOGY}</Button>
          </div>
          <Form className="ts-gt-import-form" onSubmit={this.importTopology}>
            <Form.Input id="import-topology-input-file" type="file" accept=".json" />
            <Button className="ts-gt-action-button" size="small" compact fluid color="teal" type="submit" content={Strings.IMPORT} />
          </Form>
        </Segment>
      );
    }

    if (this.state.activeTopologySidebarTab === "device") {
      return (
        <Segment className="ts-gt-panel ts-gt-dock-panel ts-gt-selected-device-panel" style={{ textAlign: "left" }}>
          <div className="ts-gt-panel-title">
            <Icon name="hdd outline" />
            <span>{Strings.SELECTED_DEVICE}</span>
          </div>
          <div style={this.getNoDeviceSelectedStyle(this.state.selectedDevice.mac)}>
            <Header
              textAlign="center"
              style={noTaskHeaderSelectedStyle}
              as="h3"
              icon
              color="teal">
              <Icon name="hdd outline" color="teal" />
              {Strings.NO_DEVICE_SELECTED}
              <Header.Subheader>
                {Strings.CHOOSE_A_DEVICE}
              </Header.Subheader>
            </Header>
          </div>

          <div className="ts-gt-device-info" style={this.getDeviceSelectedStyle(this.state.selectedDevice.mac)}>
            <b>{Strings.NAME}</b>: {this.state.selectedDevice.name}<br />
            <b>{Strings.MODEL}</b>: {this.state.selectedDevice.model}<br />
            <b>{Strings.MANUFACTURER}</b>: {this.state.selectedDevice.manufacturer}<br />
            <b>{Strings.IP}</b>: {this.state.selectedDevice.ip}<br />
            <b>{Strings.MAC}</b>: {this.state.selectedDevice.mac}<br />
            <b>{Strings.FUNCTION}</b>: {this.state.selectedDevice.role}<br />
            <b>{Strings.CURRENT_PING}</b>: {this.getPingText(this.state.selectedDevice.pingCurrent)}<br />
            <b>{Strings.MAX_PING}</b>: {this.getPingText(this.state.selectedDevice.pingMax)}<br />
            <b>{Strings.HAS_TS_EXPERT_EVENT}</b> {this.getHasTsExpertEventsText(this.state.selectedDevice.hasTsExpertEvents)}
          </div>
        </Segment>
      );
    }

    return (
      <Segment className="ts-gt-panel ts-gt-dock-panel">
        <div className="ts-gt-panel-title">
          <Icon name="crosshairs" />
          <span>{Strings.POSITION}</span>
        </div>
        <p className="ts-gt-panel-help">{Strings.GRAPHIC_TOPOLOGY_POSITION_HELP}</p>
        <div className="ts-gt-button-stack">
          <Button className="ts-gt-action-button" size="small" compact fluid color="teal" onClick={this.handleEditNodesPositionClick}>{Strings.EDIT}</Button>
          <Button className="ts-gt-action-button" size="small" compact fluid color="teal" onClick={this.handleSaveNodesPositionClick}>{Strings.SAVE_TOPOLOGY_POS_STRING}</Button>
          <Button className="ts-gt-action-button" size="small" compact fluid color="teal" onClick={this.handleDeleteNodesPositionClick}>{Strings.DELETE_TOPOLOGY_POS_STRING}</Button>
        </div>
      </Segment>
    );
  };

  render() {
    const filter = this.state.formInput;

    return (
      <div className="ts-graphic-topology-page" style={networkContainerStyle} >

        <Grid className="ts-gt-toolbar" style={gridStyle} columns={3} verticalAlign="middle">

          <Grid.Column width={4}/>


          <Grid.Column width={8}>
            <Form className="ts-gt-search-form" onSubmit={this.handleSubmit} >
              <Form.Input
                id="inputform"
                placeholder={Strings.GRAPHIC_TOPOLOGY_INPUT_PLACEHOLDER}
                icon="search"
                name="formInput"
                value={filter}
                onChange={this.handleChange} />
            </Form>
          </Grid.Column>

          <Grid.Column width={4} />

        </Grid>

        <Grid className="ts-gt-layout" divided='vertically' doubling stackable>

          <Grid.Row columns={2}>

            <Grid.Column width={13} className="ts-gt-canvas-column">
              <div className="ts-gt-canvas-card">
                <div className="ts-gt-canvas-header">
                  <div>
                    <span className="ts-gt-canvas-title">{Strings.GRAPHIC_TOPOLOGY_TITLE}</span>
                    <span className="ts-gt-canvas-subtitle">{Strings.GRAPHIC_TOPOLOGY_SUBTITLE}</span>
                  </div>
                  <div className="ts-gt-canvas-badges">
                    <span><Icon name="object group outline" /> {this.state.topologyRegions.length} {Strings.GRAPHIC_TOPOLOGY_REGIONS_LOWERCASE}</span>
                    <span><Icon name="sitemap" /> {this.getTopologyDeviceCount()} {Strings.GRAPHIC_TOPOLOGY_DEVICES_LOWERCASE}</span>
                  </div>
                </div>
                <div
                  id="network"
                  className="graphic-topology-network"
                  style={networkStyle}
                />
              </div>
            </Grid.Column>

            <Grid.Column width={3} className="ts-gt-sidebar-column">
              <div className="ts-gt-dock">
                {this.renderTopologySidebarStats()}
                {this.renderTopologySidebarTabs()}
                <div className="ts-gt-dock-content">
                  {this.renderTopologySidebarContent()}
                </div>
              </div>
            </Grid.Column>
          </Grid.Row>

        </Grid>

        <Loader size='big' active={this.state.isLoadingActive}>{Strings.GRAPHIC_TOPOLOGY_WAITING}</Loader>

        <Modal basic size='mini' open={this.state.isAddDeviceModalOpen}>
          <Modal.Header content={this.state.isEditingManualDevice ? Strings.EDIT : Strings.ADD_DEVICE} />
          <Modal.Content>
            <Dropdown
              placeholder={Strings.DEVICE_DEFAULT_DROPDOWN_PLACEHOLDER}
              fluid
              selection
              scrolling
              options={typeOptions}
              value={this.state.deviceType}
              onChange={this.onTypeDropdownChange} /><br />

            <Input
              style={tagInputStyle}
              className={ADD_DEVICE_TAG_ID}
              fluid
              id={ADD_DEVICE_TAG_ID}
              placeholder={Strings.TAG_PLACEHOLDER}
              value={this.state.addDeviceTagInputValue}
              onChange={this.onChangeDeviceInput} />

            <Input
              className={ADD_DEVICE_IP_ID}
              fluid
              id={ADD_DEVICE_IP_ID}
              placeholder={Strings.IP_PLACEHOLDER}
              value={this.state.addDeviceIpInputValue}
              onChange={this.onChangeDeviceIpInput} />

          </Modal.Content>
          <Modal.Actions>
            <Button id={ADD_DEVICE_MODAL_CLOSE_ID} color='red' onClick={this.handleManualDeviceModalCancel}>
              <Icon name='remove' />
              {Strings.CANCEL}
            </Button>
            <Button id={ADD_DEVICE_MODAL_OK_ID} color='green' onClick={this.handleManualDeviceModalConfirm}>
              <Icon name='checkmark' />
              {this.state.isEditingManualDevice ? Strings.EDIT : Strings.ADD}
            </Button>
          </Modal.Actions>
        </Modal>

        <Modal basic size='mini' open={this.state.isTopologyRegionModalOpen}>
          <Modal.Header content={this.state.isEditingTopologyRegion ? Strings.GRAPHIC_TOPOLOGY_EDIT_REGION : Strings.GRAPHIC_TOPOLOGY_REGION_NAME} />
          <Modal.Content>
            <Input
              fluid
              placeholder={Strings.GRAPHIC_TOPOLOGY_REGION_NAME_PLACEHOLDER}
              value={this.state.topologyRegionNameInputValue}
              onChange={this.onTopologyRegionNameChange} />
          </Modal.Content>
          <Modal.Actions>
            <Button color='red' onClick={this.handleTopologyRegionModalCancel}>
              <Icon name='remove' />
              {Strings.CANCEL}
            </Button>
            <Button color='green' onClick={this.handleTopologyRegionModalConfirm}>
              <Icon name='checkmark' />
              {this.state.isEditingTopologyRegion ? Strings.EDIT : Strings.ADD}
            </Button>
          </Modal.Actions>
        </Modal>

        <Modal style={saveTopologyModal} open={this.state.isSaveTopologyModalOpen}>
          <Modal.Header content={Strings.EXPORT} />
          <Modal.Content>
            {Strings.EXPORT_SVG_POPUP_MSG_2}
            <Image centered src={saveTopologyImg} />
          </Modal.Content>
          <Modal.Actions>
            <Button color="teal" onClick={() => { this.handleSaveTopologyModalButtonClick(); }}>
              <Icon name="checkmark" />
              {Strings.OK_STRING}
            </Button>
          </Modal.Actions>
        </Modal>
      </div>
    );
  }
}


const networkContainerStyle = {
  textAlign: "center"
};

const networkStyle = {
  height: "100%"
};

const gridStyle = {
  marginBottom: "-20pt"
};


const tagInputStyle = {
  marginBottom: "1vh"
};

const saveTopologyModal = {
  marginTop: "10vh",
  marginLeft: "25vw",
  height: "665px",
  textAlign: "center"
};

const noTaskHeaderSelectedStyle = {
  marginTop: "2vh"
};

const topologyDisplayOptionsStyle = {
  textAlign: "left",
  marginTop: "6px",
  marginBottom: "-4px"
};


export default withRouter(GraphicTopology);
